import {
  withLegacyToleranceNames,
  answerSymmetry,
  angleModeFor, addTiltVersion, applyTiltVersion, removeTiltVersion, versionRollKey, writeTiltVersion, type FieldVersion,
  visibleTargeted, displayEvaluation, computeTiltAcceptedRanges, evaluateTilt, isTiltDefaultIncorrectUnreachable, computeTiltDefaultIncorrectGaps, isTiltFeedbackFullySubsumed, doesTiltFeedbackOverlapAccepted, findTiltOverlappingAnswerPairs, findTiltOverlappingFeedbackPairs, hasVariableChip, sanitizeTriggerValue, probeRoll, computeFormulaExtrema, triggerFault, analyseOverlapAcrossRolls, chipNamesIn, resolveTriggerValues, forEachRoll, OVERLAP_ENUMERATION_MAX_COMBOS, NO_SENSOR_DATA_TRIGGER, SET_ANSWER_TRIGGER, TILT_OFF_AXIS_CUTOFF_DEG, type SensorHealth, type GradingMode, type TiltBlockData, type FeedbackState, type TiltInterval, type TiltBoundedInterval, type TriggerFault, TILT_DOMAIN_HI, TILT_DOMAIN_LO, type TiltVisibilityMode, rollGravityFor, type ScenarioFieldBranch, type DisplayVersion, writeScenarioRow, applyScenarioRow, remapVersionsToScenarios, syncScenarioBranches, type ScenarioLeaderVersion, type ScenarioRowTemplate, scenarioInForce, markScenarioReviewed, unreviewedCount, TILT_VERSION_KEYS , type DrivenFollower } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical,
  Trash2,
} from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useRolledWithRecalls } from './answer-ref-context';
import { useArmedDelete } from './block-actions';
import { betweenInversionVerdict, type BlockIssue, incompleteBetweenRowIds, type IssueCache, type IssueContext, IssuesPanel, memoizedByKey, triggerFaultMessage, triggerRangeInversionVerdict, useIssueContext } from './block-issues';
import { TiltIcon } from './block-library';
import { BlockQuestionEditor } from './block-question-editor';
import { EmptyQuestionNote, CollapsedInputBlock, CollapsedBlockBar, MobileOnlyChip, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { answerBarHasContent, AnswerBarText, activeIntervalIds, ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass, UnreachableChip } from './eval-chip';
import { ChipDivider, FeedbackCard, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, chipEvalIcon, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { evaluateFormulaRaw } from './formula-eval';
import { FormulaField, FormulaWithValues, resolvedTriggerChip } from './formula-field';
import { gradingModes, positiveSlotTitleFor } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { fmtDegrees, fmtLocale } from './number-format';
import { OptionsSection } from './options-section';
import { ScenarioLinkMenu } from './scenario-link-menu';
import { ScenarioRemapModal } from './scenario-remap-modal';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { ScenarioStimulus } from './scenario-stimulus';
import { SensorAdvisory } from './sensor-advisory';
import { SensorFeedbackCard } from './sensor-feedback-card';
import { SensorManualBypass } from './sensor-manual-bypass';
import { SensorAnnounce, SensorCaptureButton, sensorReadoutTone } from './sensor-readout';
import { SensorSimulateBar, type SensorScenarioOption, type SensorSourceMode } from './sensor-simulate-bar';
import { useSensorSimulator } from './sensor-simulator-context';
import { TOLERANCE_HINT_CLASS, toleranceHint } from './tolerance-hint';
import { SymmetryToggle, ToleranceRow } from './tolerance-row';
import { TriggerKindSelect, triggerKindOf } from './trigger-kind';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useTiltSensor } from './use-device-gravity';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, retriggerPositiveSlot } from './use-input-block-feedback';
import { useRestoredData } from './use-restored-data';
import { useScenarioRowProjection } from './use-scenario-rows';
import { answerPositionsKey, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, BlockPreviewProvider, rollVariableValues } from './variable-chip-preview';
import { VariableTestController } from './variable-test-controller';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { useVersionInForce } from './version-in-force';
import { VersionSelector } from './version-selector';


import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { BetweenAnswerRow } from '@/components/task/between-answer-row';
import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { RadioGroup } from '@/components/ui/radio-group';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { SpokenPlaceholder } from '@/components/ui/spoken-placeholder';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { catchAllChips, catchAllTitle, neutralChips, tiltAcceptedRangeConfig, tiltChips, formatTiltRange } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { tiltTargetedChipLabel, tiltTriggerSummary, stateChipLabel, answerExprTexts} from '@/lib/block-state-labels';
import { effectiveSetting, settingAvailable } from '@/lib/mode-gated-settings';

/** The raw bound values a trigger carries, for `resolvedTriggerChip`. Reads the stored string
 * rather than a parsed number, because the whole point is to find the formula text as authored. */
function triggerValuesOf(trigger: string): string[] {
  const parts = trigger.replace(/^[ab]:/, '').split(':');
  return parts.slice(1);
}
// ─── Types ────────────────────────────────────────────────────────────────────

interface TiltBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped by Preview's page-level Submit — see the effect that watches it. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

type TiltConditionType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'between';
type TiltTriggerType = 'exact' | 'lt' | 'lte' | 'gt' | 'gte' | 'range';

interface ExactAnswer {
  id: string; value: string; toleranceValue: string; toleranceAbove: string; toleranceBelow: string;
  /** Per answer since the tolerance move. Absent on answers authored before it, which fall
   *  back to the block's own field — always read through `answerSymmetry`, never directly. */
  toleranceSymmetry?: 'symmetric' | 'asymmetric';
}
interface BetweenAnswer { id: string; low: string; high: string }

/**
 * The valid input domain for every target/threshold/range-bound field: 0 is flat with the screen
 * up, 90 is on edge, 180 is flat with the screen down.
 *
 * Fixed, like Rotation's and unlike slider's teacher-configurable min/max. An earlier draft made
 * it follow a `measure` setting so the block could report a SIGNED lean — removed, because a sign
 * needs a named axis, a named axis needs the phone held a particular way round, and a clinometer
 * that fails when the phone is turned is not a clinometer.
 */
const TILT_MIN = TILT_DOMAIN_LO;
const TILT_MAX = TILT_DOMAIN_HI;

/**
 * The largest tolerance that means anything on this domain.
 *
 * 180 reaches every value from any centre, since that is the whole span end to end. Rotation
 * caps at 360 for the same reason expressed on a loop.
 *
 * A WARNING, not an error, matching Rotation and Compass: evaluation copes fine, it simply
 * accepts everything, which is a consequence like feedback-subsumed rather than malformed input.
 */
const TILT_MAX_TOLERANCE = 180;

// Editor-local shape — every numeric field is a string during editing (matches every other
// input block in this file set), parsed only at validation/push time. Distinct name from
// @agavi/blocks' TiltBlockData (whose fields are already-parsed numbers) since this is what
// the teacher is actively typing, not what gets evaluated.
export interface TiltBlockEditorData extends DrivenFollower {
  /**
   * Per-field angle modes, keyed by `fieldId` — what the in-field toggle writes.
   *
   * This block has no block-wide `angleMode` and never did: the ANGLE it measures is degrees of
   * arc, which is a fact about the instrument. How a teacher's `sin()` reads its argument is a
   * separate question, and that is what this answers — per formula field, as everywhere else.
   */
  angleModes?: Record<string, 'degrees' | 'radians'>;
  /** This block's question bank — see `versions.ts`. A version carries the accepted ANSWERS and
   *  the error model; the visibility mode stays shared (§3.2). */
  versions?: FieldVersion<{ states: FeedbackState[] }>[];
  /** Following a scenario: the leader, and one answer setup per variant. A value block's row
   *  carries `TILT_VERSION_KEYS` flat, exactly as a version does — see `ScenarioFieldBranch`.
   *  Mutually exclusive with `versions` (§2.2). */
  followsBlockId?: string;
  scenarioBranches?: ScenarioFieldBranch<{ states: FeedbackState[] }>[];
  question: string;
  /** Capture, or capture-on-submit for an eyeball test. No live mode — see `TiltVisibilityMode`. */
  visibilityMode: TiltVisibilityMode;
  conditionType: TiltConditionType;
  answers: ExactAnswer[];
  toleranceSymmetry: 'symmetric' | 'asymmetric';
  /** Show the student how precisely to orient. Precision, never the angle itself — see the same
   *  field on Numerical for the rule, which is why it is offered on `exact` only. */
  showTolerance: boolean;
  betweenAnswers: BetweenAnswer[];
  threshold: string;
  gradingMode: GradingMode;
  bypassEnabled: boolean;
  feedback: { states: FeedbackState[] };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────


function num(s: string): number {
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}


const ONE_SIDED: TiltConditionType[] = ['lt', 'lte', 'gt', 'gte'];

const CONDITION_TYPES: { id: TiltConditionType; symbol: string }[] = [
  { id: 'exact', symbol: '=' },
  { id: 'lt', symbol: '<' },
  { id: 'lte', symbol: '≤' },
  { id: 'gt', symbol: '>' },
  { id: 'gte', symbol: '≥' },
  { id: 'between', symbol: '↔' },
];

const CONDITION_ARIA_KEYS: Record<TiltConditionType, string> = {
  exact: 'conditionExact', lt: 'conditionLt', lte: 'conditionLte', gt: 'conditionGt', gte: 'conditionGte', between: 'conditionBetween',
};

// Blank, not pre-filled — a guessed value looks like a real answer and a teacher has to
// notice and clear it before typing their own; an empty field has no such trap. Matches
// slider-block.tsx's makeExactAnswer/makeBetweenAnswer convention exactly.
function makeExactAnswer(): ExactAnswer {
  return { id: crypto.randomUUID(), value: '', toleranceValue: '', toleranceAbove: '', toleranceBelow: '' };
}
function makeBetweenAnswer(): BetweenAnswer {
  return { id: crypto.randomUUID(), low: '', high: '' };
}

function parseAnswers(rawAnswers: Partial<ExactAnswer>[] | undefined): ExactAnswer[] {
  return rawAnswers?.length
    ? rawAnswers.map(a => ({
        id: a.id ?? crypto.randomUUID(), value: a.value ?? '',
        toleranceValue: a.toleranceValue ?? '', toleranceAbove: a.toleranceAbove ?? '', toleranceBelow: a.toleranceBelow ?? '',
        // Carried, never defaulted: `undefined` here MEANS "fall back to the block", and
        // substituting a literal would strand every answer authored before the move.
        toleranceSymmetry: a.toleranceSymmetry,
      }))
    : [makeExactAnswer()];
}
function parseBetween(rawBetween: Partial<BetweenAnswer>[] | undefined): BetweenAnswer[] {
  return rawBetween?.length
    ? rawBetween.map(a => ({ id: a.id ?? crypto.randomUUID(), low: a.low ?? '', high: a.high ?? '' }))
    : [makeBetweenAnswer()];
}

function initData(rawInput: Record<string, unknown>): TiltBlockEditorData {
  /* Blocks saved before the rename say `errorValue`; read under the current names. A missed
     one would not crash — `?? ''` turns it into an empty tolerance, and an empty tolerance
     grades EXACT, so every authored band would quietly become a point. */
  const raw = withLegacyToleranceNames(rawInput);
  return {
    /*
     * Builds a NEW object field by field, so anything not named is dropped — which is how a bank
     * comes to be created, written to Mongo and then silently gone.
     *
     * Each version's ANSWERS go through the same parsers the block's own do. They are structured
     * rows, not a plain map, and a stored row can predate a field: without this an answer saved
     * before `toleranceAbove`/`toleranceBelow` existed reached the editor with them undefined and the
     * answer-setup fields threw on `.trim()`. The roles blocks never needed this because a roles
     * map has nothing to normalise.
     */
    versions: (raw.versions as TiltBlockEditorData['versions'])?.map(v => ({
      ...v,
      answers: parseAnswers(v.answers as Partial<ExactAnswer>[] | undefined),
      betweenAnswers: parseBetween(v.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    })),
    /* Named here or silently dropped — see the note above. A row's answers go through the
       same parsers a version's do: skipping it threw on an undefined tolerance once already. */
    followsBlockId: raw.followsBlockId as string | undefined,
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as string | undefined,
    scenarioBranches: (raw.scenarioBranches as TiltBlockEditorData['scenarioBranches'])?.map(r => ({
      ...r,
      answers: parseAnswers(r.answers as Partial<ExactAnswer>[] | undefined),
      betweenAnswers: parseBetween(r.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    })),

    question: (raw.question as string) ?? '',
    visibilityMode: raw.visibilityMode === 'capture_on_submit' ? 'capture_on_submit' : 'capture',
    conditionType: (CONDITION_TYPES.some(c => c.id === raw.conditionType) ? raw.conditionType : 'exact') as TiltConditionType,
    angleModes: raw.angleModes as Record<string, 'degrees' | 'radians'> | undefined,
    answers: parseAnswers(raw.answers as Partial<ExactAnswer>[] | undefined),
    toleranceSymmetry: raw.toleranceSymmetry === 'asymmetric' ? 'asymmetric' : 'symmetric',
    showTolerance: (raw.showTolerance as boolean) ?? false,
    betweenAnswers: parseBetween(raw.betweenAnswers as Partial<BetweenAnswer>[] | undefined),
    // Blank, not pre-filled — same "not configured yet" reasoning as every other field here.
    threshold: raw.threshold != null ? String(raw.threshold) : '',
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string) ? raw.gradingMode : 'graded') as GradingMode,
    bypassEnabled: raw.bypassEnabled === true,
    feedback: (raw.feedback ?? { states: [] }) as TiltBlockEditorData['feedback'],
  };
}

// Same colon-delimited trigger format rotation.ts's parseValueTrigger expects ("lt:20",
// "range:5:15", etc.), applied directly to the submitted angle value.
function parseTiltTrigger(trigger: string): { type: TiltTriggerType; v1: string; v2: string } {
  const parts = trigger.split(':');
  const raw = parts[0] ?? 'lt';
  const type = (['exact', 'lt', 'lte', 'gt', 'gte', 'range'].includes(raw) ? raw : 'lt') as TiltTriggerType;
  return { type, v1: parts[1] ?? '', v2: parts[2] ?? '' };
}
// Values are sanitised, not trusted: a trigger is ONE delimited string, so a colon inside a value
// silently re-splits it into a different trigger. See sanitizeTriggerValue.
function buildTiltTrigger(type: TiltTriggerType, v1: string, v2: string): string {
  const a = sanitizeTriggerValue(v1), b = sanitizeTriggerValue(v2);
  return type === 'range' ? `range:${a}:${b}` : `${type}:${a}`;
}

const TRIGGER_TYPES: { id: TiltTriggerType; symbol: string }[] = [
  { id: 'exact', symbol: '=' }, { id: 'lt', symbol: '<' }, { id: 'lte', symbol: '≤' },
  { id: 'gt', symbol: '>' }, { id: 'gte', symbol: '≥' }, { id: 'range', symbol: '↔' },
];

const TRIGGER_ARIA_KEYS: Record<TiltTriggerType, string> = {
  exact: 'triggerTypeExact', lt: 'triggerTypeLt', lte: 'triggerTypeLte',
  gt: 'triggerTypeGt', gte: 'triggerTypeGte', range: 'triggerTypeRange',
};

// Each condition gets its own distinct phrasing (symbol + value) — matches slider.ts's own
// targeted-feedback triggerSummary exactly, not the old distance-style "Within"/"Farther
// than" pairing (which only made sense when triggers applied to a derived error, not the
// raw value directly).
// Identical to the chip label by contract (see block-state-labels.ts).
function tiltTargetedTitle(trigger: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  return tiltTargetedChipLabel(trigger, undefined, t, 'en');
}

// A block counts as "not set up yet" if it has no plausible correct answer at all — either
// nothing has been entered, or everything entered falls entirely outside [0, 180]. Mirrors
// slider-block.tsx's isAnswerSetupIncomplete, with a fixed domain where slider has a
// teacher-configurable min/max.
/**
 * Is there no usable answer here yet?
 *
 * `num()` returns NaN for an equation, and NaN fails every comparison, so a perfectly good answer of
 * `{test}` was read as no answer at all — which switched off the editor's answer bar, among eight
 * other things this gates. A chip formula IS a configured answer; whether the value it resolves to
 * lands inside the domain is a question about a ROLL, and the per-roll validation already asks it
 * (and warns, and wraps). Asking it here, against a value that does not exist yet, can only ever
 * give the wrong answer.
 */
function isTiltSetupIncomplete(d: TiltBlockEditorData): boolean {
  const set = (v: string) => v.trim() !== '';
  const inDomain = (v: string, lo: number, hi: number) =>
    hasVariableChip(v) || (num(v) >= lo && num(v) <= hi);
  if (d.conditionType === 'exact') {
    return !d.answers.some(a => set(a.value) && inDomain(a.value, TILT_MIN, TILT_MAX));
  }
  if (d.conditionType === 'between') {
    return !d.betweenAnswers.some(a => set(a.low) && set(a.high)
      && inDomain(a.high, TILT_MIN, Infinity) && inDomain(a.low, -Infinity, TILT_MAX));
  }
  if (!set(d.threshold)) return true;
  return !inDomain(d.threshold, TILT_MIN, TILT_MAX);
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical aria-hidden='true' className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI (targeted/targeted feedback) ─────────────────────────────────

function TiltTriggerEditor({ trigger, onChange, t, variables, rolledValues, onPlainFieldFocus, gradingMode, answerConditionChips, answerTaken, fieldId }: {
  /** The angle-mode key for this trigger's fields. A range's two bounds share it, because two
   *  bounds of one measurement disagreeing about degrees is never what anyone meant. */
  fieldId: string;
  /** Releases the shared rich-text toolbar — see FormulaField's `onFocus`. */
  onPlainFieldFocus: () => void;
  trigger: string; onChange: (t: string) => void; t: (k: string, opts?: Record<string, unknown>) => string;
  variables: TaskVariable[]; rolledValues: Record<string, string>;
  /** Another card already points at the answer — see `TriggerKindSelect`. */
  answerTaken: boolean;
  gradingMode: GradingMode;
  /** The authored condition, rendered by the parent — it owns the data and the roll. */
  answerConditionChips: React.ReactNode;
}) {
  const { type: trigType, v1, v2 } = parseTiltTrigger(trigger);
  const loNum = parseFloat(v1), hiNum = parseFloat(v2);
  const rangeInverted = trigType === 'range' && v1 !== '' && v2 !== ''
    && Number.isFinite(loNum) && Number.isFinite(hiNum) && loNum > hiNum;

  const kind = triggerKindOf(trigger);
  return (
    <div className='flex w-full min-w-0 flex-1 flex-col gap-2'>
      {/* The SHARED Answer/Custom selector, as GPS and Compass have. Survey only — see
          `trigger-kind.tsx`. Its absence here was an oversight, not a decision: the evaluator has
          resolved `set_answer` since the block was ported, so the preset worked and simply could
          not be reached. */}
      <TriggerKindSelect
        trigger={trigger}
        gradingMode={gradingMode}
        answerTaken={answerTaken}
        onChange={onChange}
        customDefault={buildTiltTrigger('exact', '', '')}
      />
      {/* The PRESET shows the authored condition where a custom one appears, read only: the
          teacher sees what it will match, and editing the answer upstream moves it. */}
      {kind === 'answer' ? (
        <div className='pointer-events-none opacity-60'>{answerConditionChips}</div>
      ) : (
      <>
      <div role='group' aria-label={t('blocks.conditions.typeLabel')} className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
        {TRIGGER_TYPES.map(({ id, symbol }) => (
          <button
            key={id}
            type='button'
            aria-pressed={trigType === id}
            aria-label={t(`blocks.tilt.${TRIGGER_ARIA_KEYS[id]}`)}
            onClick={() => onChange(buildTiltTrigger(id, v1, v2))}
            className={`flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors ${
              trigType === id ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted hover:text-ws-text'
            }`}
          >
            {symbol}
          </button>
        ))}
      </div>
      {trigType === 'range' ? (
        <>
          {/* STACKED, matching slider's own trigger pair: side by side, each bound lost roughly
              half its width to the variables picker and the degree suffix, and a bound can now hold
              a chip-rendered formula that does not fit what was left. */}
          <div className='flex flex-col gap-1.5'>
            <FormulaField
              fieldId={`${fieldId}.low`} angleModeKey={fieldId}
              hideEval
              value={v1}
              onChange={v => onChange(buildTiltTrigger('range', v, v2))}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.tilt.triggerRangeMinAria')}
              placeholder={t('blocks.triggers.triggerRangeMin')}
              suffix='°'
              hasError={rangeInverted}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
            <span aria-hidden='true' className='self-center text-xs text-ws-muted'>{t('blocks.connectors.to')}</span>
            <FormulaField
              fieldId={`${fieldId}.high`} angleModeKey={fieldId}
              hideEval
              value={v2}
              onChange={v => onChange(buildTiltTrigger('range', v1, v))}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.tilt.triggerRangeMaxAria')}
              placeholder={t('blocks.triggers.triggerRangeMax')}
              suffix='°'
              hasError={rangeInverted}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        </>
      ) : (
        <FormulaField
          fieldId={fieldId}
          hideEval
          value={v1}
          onChange={v => onChange(buildTiltTrigger(trigType, v, ''))}
          variables={variables}
          rolledValues={rolledValues}
          ariaLabel={t('blocks.tilt.triggerValuePlaceholder')}
          /* No placeholder. A greyed "Angle (°)" in an empty condition field reads as a value
                that is already there, which is the same confusion the invented `lt:10` seed caused
                one line up. The field's aria-label still names it for a screen reader; sighted
                users have the condition's own symbol immediately to its left. */
          suffix='°'
          className='w-full' 
          onFocus={onPlainFieldFocus}/>
      )}
      </>
      )}
    </div>
  );
}

function TiltTriggerChip({ trigger, evaluation, t, scenario, angleMode }: {
  /** THIS trigger's own angle mode — it is a formula field like any other. The caller looks it
   *  up with `angleModeFor`, because only the block knows the map. */
  angleMode: 'degrees' | 'radians';

  trigger: string; evaluation: FeedbackState['evaluation']; t: (k: string, opts?: Record<string, unknown>) => string; scenario: Record<string, string>;
}) {
  const chipColor =
    chipToneClass(evaluation);
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      <span className={TRIGGER_CHIP_TEXT_CLASS}>{resolvedTriggerChip({
        trigger, scenario,
        summaryOf: resolved => tiltTriggerSummary(resolved, t),
        evaluate: (expr, values) => evaluateFormulaRaw(expr, values, angleMode),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      })}</span>
    </span>
  );
}

// ─── Correct-answer condition UI (SegmentedControl + exact/between/threshold) ─────────────
// Ported from slider-block.tsx's SegmentedControl + AnswerSetupFields, unchanged in behavior
// except for the fixed [TILT_MIN, TILT_MAX] domain in place of a teacher-configurable min/max.

interface AnswerSetupFieldsProps {
  t: (k: string, opts?: Record<string, unknown>) => string;
  conditionType: TiltConditionType;
  onConditionTypeChange: (v: TiltConditionType) => void;
  answers: ExactAnswer[];
  onUpdateAnswer: (id: string, patch: Partial<ExactAnswer>) => void;
  onAddAnswer: () => void;
  onRemoveAnswer: (id: string) => void;
  /** Flips ONE answer between a single tolerance and a pair — the setting is per answer. */
  onToggleAnswerSymmetry: (id: string) => void;
  betweenAnswers: BetweenAnswer[];
  onUpdateBetweenAnswer: (id: string, patch: Partial<BetweenAnswer>) => void;
  onAddBetweenAnswer: () => void;
  onRemoveBetweenAnswer: (id: string) => void;
  threshold: string;
  onThresholdChange: (v: string) => void;
  armedAnswerId: string | null;
  setArmedAnswerId: (id: string | null) => void;
  armedBetweenId: string | null;
  setArmedBetweenId: (id: string | null) => void;
  /** Clears the shared rich-text toolbar's active-editor reference. Every plain <input> in this
   * block calls it on focus: without it the toolbar stays pointed at whichever RichTextEditor was
   * last active (the Question), so hitting Bold while the cursor sits in a numeric field silently
   * formats the question instead. Same fix MCQ/Ordering's own alt-text and label fields carry. */
  onPlainFieldFocus: () => void;
  /** Task variables offered by the answer fields' chip picker. Answer VALUES accept formulas;
   * tolerance deliberately does not — see the block's own note on why a tolerance on a loop is
   * absolute degrees only. */
  variables: TaskVariable[];
  /** The roll the answer fields preview against — `{}` in plain display, where chips stay
   * unresolved. */
  rolledValues: Record<string, string>;
}

function AnswerSetupFields({
  t, conditionType, onConditionTypeChange, answers, onUpdateAnswer, onAddAnswer, onRemoveAnswer,
  onToggleAnswerSymmetry,
  betweenAnswers, onUpdateBetweenAnswer, onAddBetweenAnswer, onRemoveBetweenAnswer,
  threshold, onThresholdChange, armedAnswerId, setArmedAnswerId, armedBetweenId, setArmedBetweenId, onPlainFieldFocus,
  variables, rolledValues,
}: AnswerSetupFieldsProps) {
  const addAnswerBtnRef = useRef<HTMLButtonElement>(null);
  const addBetweenBtnRef = useRef<HTMLButtonElement>(null);
  return (
    <>
      <SegmentedControl fieldId='conditionType'
        options={CONDITION_TYPES.map(c => ({ id: c.id, label: c.symbol, ariaLabel: t(`blocks.conditions.${CONDITION_ARIA_KEYS[c.id]}`) }))}
        value={conditionType}
        onChange={v => onConditionTypeChange(v as TiltConditionType)}
        aria-label={t('blocks.conditions.typeLabel')}
        stretch
      />

      {conditionType === 'exact' && (
        <div className='flex flex-col gap-3'>
          {answers.map((a, i) => {
            const isAsym = answerSymmetry(a) === 'asymmetric';
            const isArmed = armedAnswerId === a.id;
            // `hasVariableChip` first: `num('{n}')` is 0, which happens to sit inside both
            // domains, so this was right only by luck — and would start lying the day a domain
            // stopped containing zero. Whether a ROLL lands in range is asked per-roll elsewhere.
            const aOutOfRange = a.value.trim() !== '' && !hasVariableChip(a.value)
              && (num(a.value) < TILT_MIN || num(a.value) > TILT_MAX);
            // The tolerance fields carry the same treatment Compass's do. Reporting the cap only in
            // the issue bar left the offending box looking fine, so with several answers (and two
            // halves each when asymmetric) a teacher had to guess which one was at fault.
            const tolOver = (v: string) => v.trim() !== '' && num(v) > TILT_MAX_TOLERANCE;
            const belowOver = tolOver(a.toleranceBelow), aboveOver = tolOver(a.toleranceAbove), symOver = tolOver(a.toleranceValue);
            const toleranceWarningId = `angle-tol-warn-${a.id}`;
            const anyTolOver = belowOver || aboveOver || symOver;
            return (
              <div key={a.id} data-field-id={a.id} className={`flex flex-col gap-1 ${isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''}`}>
              <div className='flex items-center gap-2'>
                {/* No filterPositiveNumber: it strips everything but digits, which is right for a
                    tolerance and wrong for a formula. FormulaField owns its own input discipline. */}
                <FormulaField fieldId={`${a.id}.value`}
                  hideEval
                  value={a.value}
                  onChange={v => onUpdateAnswer(a.id, { value: v })}
                  variables={variables}
                  rolledValues={rolledValues}
                  ariaLabel={answers.length > 1 ? `${t('blocks.answers.fieldValue')} ${i + 1}` : t('blocks.answers.fieldValue')}
                  hasError={aOutOfRange}
                  className='min-w-0 flex-1' 
                  onFocus={onPlainFieldFocus}/>
              </div>
                {/* The tolerance has its own row in both modes, and `decimal` not `scientific`
                    because an angle runs 0..360 — see `ToleranceRow` for both calls. */}
                <ToleranceRow
                  idPrefix={a.id}
                  asymmetric={isAsym}
                  grammar='decimal'
                  toleranceValue={a.toleranceValue}
                  toleranceBelow={a.toleranceBelow}
                  toleranceAbove={a.toleranceAbove}
                  onChange={patch => onUpdateAnswer(a.id, patch)}
                  warn={{ value: symOver, below: belowOver, above: aboveOver }}
                  describedBy={toleranceWarningId}
                  onFocus={onPlainFieldFocus}
                  trailing={<>
                    <SymmetryToggle compact fieldId={`${a.id}.toleranceSymmetry`}
                      asymmetric={isAsym} onToggle={() => onToggleAnswerSymmetry(a.id)} />
                    {answers.length > 1 && (
                      <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                        aria-label={isArmed ? t('blocks.answers.confirmRemoveAnswer') : t('blocks.answers.removeAnswer')}
                        className='shrink-0'
                        onClick={() => {
                          if (isArmed) { onRemoveAnswer(a.id); setArmedAnswerId(null); requestAnimationFrame(() => addAnswerBtnRef.current?.focus()); }
                          else { setArmedBetweenId(null); setArmedAnswerId(a.id); }
                        }}
                        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedAnswerId(null); }}
                        onBlur={() => { if (isArmed) setArmedAnswerId(null); }}
                      >
                        <Trash2 className='h-4 w-4' />
                      </IconButton>
                    )}
                  </>} />
              {/* sr-only, not a visible row: this editor states a block's problems in the issue bar,
                  and the amber border already carries it visually. The text exists so
                  `aria-describedby` gives a screen-reader user what the border gives a sighted one. */}
              {anyTolOver && (
                <p id={toleranceWarningId} className='sr-only'>
                  {t('blocks.tilt.validationToleranceMax', { max: TILT_MAX_TOLERANCE })}
                </p>
              )}
              </div>
            );
          })}

          {answers.length < 5 && (
            <AddRowButton ref={addAnswerBtnRef} onClick={onAddAnswer} label={t('blocks.answers.addAnswer')} />
          )}
        </div>
      )}

      {conditionType === 'between' && (
        <div className='flex flex-col gap-3'>
          {betweenAnswers.map((a, i) => {
            const isArmed = armedBetweenId === a.id;
            const bothBoundsSet = a.low.trim() !== '' && a.high.trim() !== '';
            const rangeOutOfRange = bothBoundsSet && !hasVariableChip(a.low) && !hasVariableChip(a.high)
              && (num(a.high) < TILT_MIN || num(a.low) > TILT_MAX);
            return (
              // `data-field-id` so `between-incomplete` can reach this row. Without it the issue
              // renders as a BUTTON (any non-empty `fields` does) that silently does nothing — the
              // dead control `IssueRow` documents avoiding, and the fault numerical's unit options
              // shipped with.
              <BetweenAnswerRow
                key={a.id}
                fieldId={a.id}
                armed={isArmed}
                grouped={betweenAnswers.length > 1}
                to={t('blocks.connectors.to')}
                low={
                  <FormulaField fieldId={`${a.id}.low`} angleModeKey={a.id}
                    hideEval
                    value={a.low}
                    onChange={v => onUpdateBetweenAnswer(a.id, { low: v })}
                    variables={variables}
                    rolledValues={rolledValues}
                    ariaLabel={betweenAnswers.length > 1 ? `${t('blocks.triggers.triggerRangeMin')} ${i + 1}` : t('blocks.triggers.triggerRangeMin')}
                    placeholder={t('blocks.triggers.triggerRangeMin')}
                    hasError={rangeOutOfRange}
                    className='min-w-0 flex-1' 
                    onFocus={onPlainFieldFocus}/>
                }
                high={
                  <FormulaField fieldId={`${a.id}.high`} angleModeKey={a.id}
                    hideEval
                    value={a.high}
                    onChange={v => onUpdateBetweenAnswer(a.id, { high: v })}
                    variables={variables}
                    rolledValues={rolledValues}
                    ariaLabel={betweenAnswers.length > 1 ? `${t('blocks.triggers.triggerRangeMax')} ${i + 1}` : t('blocks.triggers.triggerRangeMax')}
                    placeholder={t('blocks.triggers.triggerRangeMax')}
                    hasError={rangeOutOfRange}
                    className='min-w-0 flex-1' 
                    onFocus={onPlainFieldFocus}/>
                }
                action={betweenAnswers.length > 1 && (
                  <IconButton type='button' variant='destructive' size='md' isArmed={isArmed}
                    aria-label={isArmed ? t('blocks.answers.confirmRemoveRange') : t('blocks.answers.removeRange')}
                    className='shrink-0'
                    onClick={() => {
                      if (isArmed) { onRemoveBetweenAnswer(a.id); setArmedBetweenId(null); requestAnimationFrame(() => addBetweenBtnRef.current?.focus()); }
                      else { setArmedAnswerId(null); setArmedBetweenId(a.id); }
                    }}
                    onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed) setArmedBetweenId(null); }}
                    onBlur={() => { if (isArmed) setArmedBetweenId(null); }}
                  >
                    <Trash2 className='h-4 w-4' />
                  </IconButton>
                )}
              />
            );
          })}
          {betweenAnswers.length < 5 && (
            <AddRowButton ref={addBetweenBtnRef} onClick={onAddBetweenAnswer} label={t('blocks.answers.addAnswer')} />
          )}
        </div>
      )}

      {ONE_SIDED.includes(conditionType) && (() => {
        const thresholdOutOfRange = threshold.trim() !== '' && (num(threshold) < TILT_MIN || num(threshold) > TILT_MAX);
        return (
          <div className='flex flex-col gap-1'>
            <FormulaField fieldId='threshold'
              hideEval
              value={threshold}
              onChange={v => onThresholdChange(v)}
              variables={variables}
              rolledValues={rolledValues}
              ariaLabel={t('blocks.answers.fieldValue')}
              placeholder={t('blocks.answers.fieldValue')}
              hasError={thresholdOutOfRange}
              className='w-full' 
              onFocus={onPlainFieldFocus}/>
          </div>
        );
      })()}
    </>
  );
}

// ─── Collapsed-view display ────────────────────────────────────────────────────

// The editor-local string shape converted into the parsed-number config rotation.ts' interval math
// expects — same purpose as compass-block.tsx's own compassAcceptedRangeConfig.
/** `resolve` turns the editor's raw formula text ("{n}*30") into the number the maths needs.
 * Defaults to `num`, so every existing caller — and any plain-number setup — behaves exactly as
 * before. Tolerances deliberately never resolve: a tolerance on a loop is absolute degrees, not an
 * equation. Same bridge shape slider and numerical already use. */
/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists several. Shared by both non-editing render paths (the collapsed
 * card's test widget and the student-facing Preview). */
function buildNormalizedTiltData(
  rawD: TiltBlockEditorData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): TiltBlockEditorData {
  const rawVersioned = applyTiltVersion(rawD, versionId);
  return {
    ...rawVersioned,
    feedback: normalizeStandardFeedback(rawVersioned.feedback, t, rawVersioned.gradingMode, {
      defaults: { unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>` },
      // Angle's first sensor state. It had none — the only sensor block that could fail with
      // nothing to say about it, and so the only one absent from the Flow rungs entirely.
      extraDefaults: TILT_SENSOR_DEFAULTS(t),
    }),
  };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `tiltIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function tiltBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return tiltIssues(buildNormalizedTiltData(initData(block.data), ctx.t), ctx);
}

export function tiltIssues(d: TiltBlockEditorData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  const categoryFeedback = t('blocks.areas.feedback');

  // Three kinds of field, and `num()` cannot tell them apart: it returns 0 for anything unparseable,
  // so before this every FORMULA answer validated as though it were a literal 0. Same class of bug
  // `hasNum()` exists for. Each check asks what KIND of field it has before anything else.
  // The threshold is included: under lt/lte/gt/gte it IS the answer.
  /** Each answer field's text WITH its id, so a check can read that field's own angle mode. */
  const answerFieldRows: { value: string; fieldId: string }[] =
    d.conditionType === 'exact' ? d.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
    : d.conditionType === 'between'
      ? d.betweenAnswers.flatMap(a => [
        { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }])
      : [{ value: d.threshold, fieldId: 'threshold' }];
  const answerFields: string[] = answerFieldRows.map(r => r.value);
  const probeValues = probeRoll(variables);
  const isFormula = (v: string) => hasVariableChip(v);
  const anyFormulaAnswer = answerFields.some(isFormula);

  const outOfDomain = (v: string) => v.trim() !== '' && !isFormula(v) && !(num(v) >= TILT_MIN && num(v) <= TILT_MAX);
  if (answerFields.some(outOfDomain)) {
  /**
   * §4.4 — a row copied by the projection is "copied, not decided". The box shows a count
   * beside the pager for the row you are on; this is the half that reaches the publication
   * gate, so a teacher who never pages to scenario 5 still learns it was never reviewed.
   */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
    issues.push({ key: 'answer-range', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.tilt.validationValueOutOfRange') });
  }

  // Judged against a PROBE roll from the variables' own domains, never a live roll: an unselected
  // card has no roll, so every formula would read NaN and be called broken for being looked at from
  // the wrong surface.
  if (answerFieldRows.some(r => r.value.trim() !== '' && isFormula(r.value)
    && !Number.isFinite(evaluateFormulaRaw(r.value, probeValues, angleModeFor(d, r.fieldId))))) {
    issues.push({ key: 'answer-formula-invalid', severity: 'error', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.answers.issueAnswerFormulaInvalid') });
  }

  // A resolved target outside [0, 360) WRAPS — 390 is 30 — so this warns rather than errors.
  // Quantified over the extrema: judged on one roll it would appear and vanish as the dice turn.
  if (anyFormulaAnswer) {
    const extrema = computeFormulaExtrema({
      conditionType: d.conditionType, answers: d.answers, betweenAnswers: d.betweenAnswers,
      threshold: d.threshold, toleranceMode: 'absolute', toleranceSymmetry: d.toleranceSymmetry, angleMode: 'degrees',
    }, variables);
    /*
     * An answer that can never land in the domain.
     *
     * Rotation warns about "beyond a full turn" instead, which is a statement about a loop and
     * has no meaning here. The pose equivalent is stricter and more useful: the domain has real
     * ends, so a formula answer whose whole reachable range sits outside them grades nothing, and
     * says so while looking configured.
     *
     * The ENVELOPE is the right thing to test here, unlike the band-width check below —
     * `computeFormulaExtrema` answers "how far can this reach", and if even its furthest reach
     * misses the domain then no roll can land inside it.
     */
    const DLO = TILT_MIN, DHI = TILT_MAX;
    if (extrema && (extrema.hi < DLO || extrema.lo > DHI)) {
      issues.push({ key: 'answer-outside-domain', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
        detail: t('blocks.tilt.issueAnswerOutsideDomain') });
    }
    /**
     * A band that spans the whole DOMAIN for SOME student — measured as the band's own WIDTH at
     * each roll, not as the envelope of where it can sit across rolls.
     *
     * The envelope was the bug. `computeFormulaExtrema` answers "how far can this reach", so an
     * answer of `{n}` over 1..400 reported a 399° span and tripped this — while every individual
     * student got a single heading with no tolerance at all. The block was graded perfectly well;
     * only the check thought otherwise.
     *
     * Restricted to `between`, because that is the only shape whose width can vary with the roll.
     * An exact answer's width is twice its tolerance, which does not depend on any variable and is
     * already reported, more precisely, by `tolerance-max`.
     */
    const bandWidth = (vals: Record<string, string>) => d.betweenAnswers.some(a => {
      const lo = evaluateFormulaRaw(a.low, vals, angleModeFor(d, a.id));
      const hi = evaluateFormulaRaw(a.high, vals, angleModeFor(d, a.id));
      return Number.isFinite(lo) && Number.isFinite(hi) && Math.abs(hi - lo) >= (DHI - DLO) - 1e-9;
    });
    if (d.conditionType === 'between') {
      const bandNames = chipNamesIn(d.betweenAnswers.flatMap(a => [a.low, a.high]));
      // ANY roll, not every: a band that swallows the domain for one student in ten is still a
      // student whose answer cannot be wrong, and the teacher cannot see that from their own screen.
      let coversDomain = false;
      if (bandNames.length === 0) {
        coversDomain = bandWidth(probeValues);
      } else {
        forEachRoll(bandNames, variables, vals => {
          if (bandWidth(vals)) { coversDomain = true; return 'stop'; }
          return undefined;
        }, OVERLAP_ENUMERATION_MAX_COMBOS);
      }
      if (coversDomain) {
        issues.push({ key: 'answer-covers-domain', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
          detail: t('blocks.tilt.issueAnswerCoversDomain') });
      }
    }
  }

  // Only `exact` carries a tolerance; a `between` answer states its own two edges, and a threshold
  // condition is an open half-line with no tolerance at all. Matches Compass's own tolerance-max.
  const toleranceTooLarge = d.conditionType === 'exact' && d.answers.some(a => {
    const vals = answerSymmetry(a, d) === 'asymmetric' ? [a.toleranceAbove, a.toleranceBelow] : [a.toleranceValue];
    return vals.some(v => v.trim() !== '' && num(v) > TILT_MAX_TOLERANCE);
  });
  if (toleranceTooLarge) {
    issues.push({ key: 'tolerance-max', severity: 'warning', kind: 'defect', category: categoryAnswerSetup,
      detail: t('blocks.tilt.validationToleranceMax', { max: TILT_MAX_TOLERANCE }) });
  }

  /**
   * Two accepted answers covering the same angles.
   *
   * A warning, not an error: acceptance is their union, so the grading is right either way. What is
   * wrong is the authoring — two rows that look different mean one thing.
   *
   * Simpler than Rotation's, which needs seam-awareness because on a loop "0 +/- 2" and "360 +/- 2"
   * are one answer written two ways. There is no second spelling of a tilt, so a plain numeric
   * comparison is complete here rather than merely usually right — and the one case worth catching
   * is two bands made identical BY THE CLAMP, which falls out of comparing the clamped intervals.
   */
  // Skipped while a formula answer is present: the comparison needs concrete numbers, and with
  // `num()` turning every formula into 0 it reported two unrelated equations as one answer.
  const overlappingAnswers = anyFormulaAnswer ? [] : findTiltOverlappingAnswerPairs(tiltAcceptedRangeConfig(d));
  if (overlappingAnswers.length > 0) {
    /*
     * Whether the overlap is one the CLAMP created, which is worth saying because the authored
     * numbers do not show it: 88 +/- 5 and 95 +/- 10 look like different answers and both land on
     * the top of the domain.
     *
     * Rotation's equivalent names the SEAM instead, which is the same idea on a loop and has no
     * meaning here — there is no second spelling of a tilt. Porting that branch unchanged left
     * this reaching for a `blocks.tilt.answerOverlapSeamWarning` that was never written, which the
     * locale-key invariant could not see because the key sits inside a ternary.
     */
    const bandClamped = d.conditionType === 'exact' && d.answers.some(a => {
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const loErr = num(asym ? a.toleranceBelow : a.toleranceValue);
      const hiErr = num(asym ? a.toleranceAbove : a.toleranceValue);
      return num(a.value) - loErr < TILT_MIN || num(a.value) + hiErr > TILT_MAX;
    });
    issues.push({
      key: 'answer-overlap',
      severity: 'warning',
      kind: 'defect',
      category: categoryAnswerSetup,
      detail: t(bandClamped ? 'blocks.tilt.answerOverlapClampWarning' : 'blocks.tilt.answerOverlapWarning'),
    });
  }

  /*
   * Quantified. This read `num(a.low) > num(a.high)`, and `num()` returns 0 for a formula — so for
   * equation bounds the test was `0 > 0` and the check could not fire even in principle. It worked
   * only for literals, which are the case least likely to be authored backwards by accident.
   */
  // A `between` row with exactly one bound filled is DROPPED by the evaluator, so it grades as
  // though it were not there. This block gated only on "no row is complete", which one complete row
  // satisfies — so a half-filled row beside a complete one was silent. See `incompleteBetweenRowIds`.
  if (d.conditionType === 'between') {
    const halfRows = incompleteBetweenRowIds(d.betweenAnswers);
    if (halfRows.length > 0) {
      issues.push({ key: 'between-incomplete', fields: halfRows, severity: 'error', kind: 'incompletion',
        category: categoryAnswerSetup, detail: t('blocks.answers.issueBetweenIncomplete') });
    }
  }

  const tiltVerdict = d.conditionType === 'between'
    ? betweenInversionVerdict(d.betweenAnswers, variables, 'degrees')
    : 'never';
  if (tiltVerdict === 'always') {
    issues.push({ key: 'answer-range-inverted', severity: 'error', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.conditions.betweenInverted') });
  } else if (tiltVerdict === 'sometimes') {
    issues.push({ key: 'answer-range-may-invert', severity: 'warning', kind: 'defect', category: categoryAnswerSetup, detail: t('blocks.conditions.betweenMayInvert') });
  } else if (tiltVerdict === 'unknown') {
    issues.push({ key: 'answers-unverifiable', severity: 'warning', kind: 'unverified', category: categoryAnswerSetup, detail: t('blocks.answers.issueTooWideToVerify') });
  }


  if (!d.question.replace(/<\/?p>/g, '').trim()) {
    issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  }

  // A variable chip in the QUESTION with a fixed answer is silent mis-grading: every student reads a
  // different target and every student is graded against the same one. No sensor block could detect
  // this — `htmlHasVariableChips` was called zero times in all three. A defect, not an incompletion:
  // both halves are authored, and more typing in the question will not resolve it.
  //
  // The "answer can vary" side is written against the mechanism each block actually has, so this
  // stays correct as those mechanisms land: chips in an answer field for compass/angle, a configured
  // branch set for gps.
  // The `&& !answerOutOfRange` guard is gone: `supersededBy` is the one mechanism for standing a
  // catch-all down, and doing it twice meant every new key had to be added in two places.
  // Graded only: optional and survey author no answer key, so a missing one is the mode
  // working as intended rather than a setup the teacher has not finished.
  if (d.gradingMode === 'graded' && isTiltSetupIncomplete(d)) {
    issues.push({ key: 'setup-incomplete', severity: 'error', kind: 'incompletion', supersededBy: ['answer-range', 'answer-formula-invalid', 'answer-range-inverted', 'answer-overlap', 'tolerance-max'], category: categoryAnswerSetup, detail: t('blocks.tilt.barSetupIncomplete') });
  }

  const targetedStates = d.feedback.states.filter(s => s.type === 'targeted');
  const triggers = targetedStates.map(s => s.trigger);
  /** A trigger's own field, so the overlap analysis reads it in the mode it was authored in.
   *  Without this every trigger resolved in the block default and a radian trigger looked
   *  identical to a degree answer — reported as "completely overlaps Correct". */
  const triggerFieldId = new Map(targetedStates.map(s => [s.trigger, `feedback.${s.id}.trigger`] as const));
  if (triggers.some(trig => {
    const { v1, v2 } = parseTiltTrigger(trig);
    return outOfDomain(v1) || outOfDomain(v2);
  })) {
    issues.push({ key: 'trigger-range', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.tilt.validationValueOutOfRange') });
  }
  if (triggers.some(trig => {
    const { type, v1, v2 } = parseTiltTrigger(trig);
    return type === 'range' && v1 !== '' && v2 !== '' && Number.isFinite(num(v1)) && Number.isFinite(num(v2)) && num(v1) > num(v2);
  })) {
    issues.push({ key: 'trigger-range-inverted', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.conditions.betweenInverted') });
  }
  /*
   * The FORMULA case, which the literal check above cannot see: `num()` returns 0 for an equation,
   * so `{a} > {b}` is `0 > 0` and never fires. Same blindness its answer check had.
   *
   * A WARNING, not an error, even for `always`: an inverted feedback band means that state never
   * fires and the student falls through to Default Incorrect — still graded, just less specific —
   * whereas an inverted ANSWER band means nobody can be right. The blocks' reachability machinery
   * is what says "this state cannot fire".
   */
  const trigVerdict = triggerRangeInversionVerdict(triggers, variables, 'degrees');
  if (trigVerdict === 'always' || trigVerdict === 'sometimes') {
    issues.push({ key: 'trigger-range-may-invert', severity: 'warning', kind: 'defect', category: categoryFeedback,
      detail: t('blocks.conditions.betweenMayInvert') });
  }


  /**
   * Every fault the shared detector finds in a targeted condition.
   *
   * `allowWrap` is the one thing that differs between the blocks that use this, and Tilt is the
   * clearest case of all: its domain is an open segment, so there is no seam for a range to
   * straddle and no reading under which a high-before-low pair is a deliberate answer. An inverted
   * range is simply an error. Compass allows the wrap; Rotation refuses it on a loop that has one.
   */
  const triggerFaults = d.feedback.states
    .filter(s => s.type === 'targeted')
    .map(s => triggerFault(s.trigger, {
      // The same resolver the answer fields are judged with, a few lines above.
      resolve: (v: string) => evaluateFormulaRaw(v, probeValues, angleModeFor(d, `feedback.${s.id}.trigger`)),
      declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(probeValues)),
      allowWrap: false,
    }))
    .filter((f): f is TriggerFault => f !== null);

  if (triggerFaults.includes('unresolvable')) {
    issues.push({ key: 'trigger-unresolvable', severity: 'error', kind: 'defect', category: categoryFeedback, detail: t('blocks.conditions.issueUnresolvable') });
  }
  if (triggerFaults.includes('no-value')) {
    issues.push({ key: 'trigger-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.conditions.issueNoValue') });
  }
  if (triggerFaults.includes('range-incomplete')) {
    issues.push({ key: 'trigger-range-incomplete', severity: 'error', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.conditions.issueRangeIncomplete') });
  }

  if (hasEmptyFeedbackContent(d.feedback.states)) {
    issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });
  }

  if (d.gradingMode === 'graded' && !isTiltSetupIncomplete(d)) {
    if (findTiltOverlappingFeedbackPairs(triggers).length > 0) {
      issues.push({ key: 'feedback-overlap', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.tilt.feedbackOverlapWarning') });
    }
    /**
     * Quantified over the roll space, not judged at one point of it.
     *
     * Two things were wrong with the single check this replaces. It resolved the answer with `num`,
     * which returns 0 for anything unparseable — so a formula answer put the accepted band around
     * ZERO and every claim below was made against a band the block does not have. And even with a
     * resolver it would have been one roll's answer about a band that moves with the dice.
     */
    const overlap = analyseOverlapAcrossRolls({
      triggers, variables,
      names: chipNamesIn([...answerFields, ...triggers]),
      atRoll: vals => {
        const resolveAt = (v: string, fieldId = '') => evaluateFormulaRaw(v, vals, angleModeFor(d, fieldId));
        const ranges = computeTiltAcceptedRanges(tiltAcceptedRangeConfig(d, resolveAt));
        if (ranges.length === 0) return null;
        const at = (trig: string) =>
          resolveTriggerValues(trig, v => resolveAt(v, triggerFieldId.get(trig) ?? ''));
        return {
          subsumed: trig => isTiltFeedbackFullySubsumed(at(trig), ranges),
          overlaps: trig => doesTiltFeedbackOverlapAccepted(at(trig), ranges),
        };
      },
    });
    // Graded only. Every verdict here is about the ACCEPTED BAND, and in a participation mode
    // there is no band in force — correct is never checked, so nothing is subsumed by it and
    // nothing overlaps it. Reporting these outside graded would warn about a condition the
    // evaluator will happily fire.
    if (d.gradingMode === 'graded' && overlap.any.alwaysSubsumed) {
      issues.push({ key: 'feedback-subsumed', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.tilt.feedbackSubsumedWarning') });
    }
    if (d.gradingMode === 'graded' && overlap.any.alwaysOverlaps) {
      issues.push({ key: 'feedback-overlaps-correct', severity: 'warning', kind: 'defect', category: categoryFeedback, detail: t('blocks.tilt.feedbackOverlapsCorrectWarning') });
    }
    // The collision a teacher cannot see, because the roll in front of them does not contain it.
    if (d.gradingMode === 'graded' && overlap.any.mayCollide) {
      issues.push({ key: 'feedback-may-overlap-correct', severity: 'warning', kind: 'defect',
        supersededBy: ['feedback-overlaps-correct', 'feedback-subsumed'],
        category: categoryFeedback, detail: t('blocks.conditions.mayOverlapCorrect') });
    }
  }

  return issues;
}

const tiltIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();

// ─── Live test: scenario-driven simulated readings ─────────────────────────────
// See compass-block.tsx's own header comment for why this is scenario-driven rather than a
// free-typed field. Angle needs it most of the three: a teacher can walk to a location and can
// physically turn to face a bearing, but cannot hold a laptop at an arbitrary inclination while
// authoring, so without simulation this block is effectively untestable at authoring time.
//
/**
 * Simulated SENSOR FAULTS, and nothing else.
 *
 * Value scenarios earn nothing here: a tilt is a single number the teacher can type into the box
 * beside this dropdown. What survives is what the box cannot express. GPS keeps a full preset list
 * for the opposite reason — a point 10 m off target is a coordinate pair, not something anyone
 * types.
 *
 * ONE, where Rotation has three. Rotation's list maps onto a tracked gesture, which can fail in
 * ways a pose cannot: a broken stream costs it the turn count and the calibration, and a drifting
 * phone leaves the pivot plane. A pose has no history to lose and no axis to leave, so the three
 * collapse to the only thing that can actually go wrong — no reading at all.
 *
 * An earlier draft kept all three ids and gave two of them the same label, which put "No Reading"
 * in the dropdown twice.
 */
type TiltScenarioId =
  /** No reading at all — the only route to No Answer, since Capture on Submit takes one
   * automatically whenever there is one to take. Covers every way a device can fail to measure:
   * no motion sensor, permission refused, or no secure context. */
  | 'noReading'
  /** The phone held DIAGONALLY, between its two axes. A reading still exists and looks entirely
   * plausible — it is simply understating the slope, which is what makes this worth arming. */
  | 'offAxis';

/** The authored answer reduced to a centre angle plus a one-sided tolerance. First configured
 * answer only — a simulated reading is a single number. */
/**
 * A centre + tolerance for EVERY authored answer, ported from Compass's compassSimBases.
 *
 * This used to `.find()` the first non-blank answer and simulate only that one, so on a multi-answer
 * block the others were never exercised by any amount of rerolling — and a scenario named for being
 * wrong could land on one of them and report Correct.
 *
 * Threshold conditions still yield exactly one entry: they accept an open half-line, so "the centre"
 * is the threshold itself and there is no symmetric tolerance — scenarios step off the boundary.
 */
/**
 * `resolve` is how a FORMULA answer gets simulated as the value it rolls to.
 *
 * Defaulting to `num` would be the bug this whole family has: `num('{n}*2')` is 0, so a block
 * whose answer is an equation simulated a reading of 0 degrees and the teacher's own scenario
 * quietly tested the wrong number. The tolerances keep `num` on purpose — a tolerance is a
 * literal, and the formula fields do not offer one.
 */
export function tiltSimBases(
  d: TiltBlockEditorData,
  /** Defaults to "literals only": a formula has no value until something rolls it, and `num()`
   *  answering 0 for one is exactly the confident-wrong-number this family keeps producing. */
  resolve: (s: string, fieldId?: string) => number = v => (hasVariableChip(v) ? NaN : num(v)),
): { center: number; tolerance: number }[] {
  if (isTiltSetupIncomplete(d)) return [];
  const usable = (v: number) => (Number.isFinite(v) ? v : NaN);
  if (d.conditionType === 'exact') {
    return d.answers.filter(a => a.value.trim() !== '').map(a => {
      const asym = answerSymmetry(a, d) === 'asymmetric';
      const above = asym ? num(a.toleranceAbove) : num(a.toleranceValue);
      const below = asym ? num(a.toleranceBelow) : num(a.toleranceValue);
      return { center: usable(resolve(a.value, `${a.id}.value`)), tolerance: Math.max(above, below, 0) };
    }).filter(b => Number.isFinite(b.center));
  }
  if (d.conditionType === 'between') {
    return d.betweenAnswers.filter(a => a.low.trim() !== '' && a.high.trim() !== '').map(a => {
      const lo = resolve(a.low, a.id), hi = resolve(a.high, a.id);
      return { center: (lo + hi) / 2, tolerance: Math.abs(hi - lo) / 2 };
    }).filter(b => Number.isFinite(b.center));
  }
  const v = resolve(d.threshold, 'threshold');
  return Number.isFinite(v) ? [{ center: v, tolerance: 0 }] : [];
}

/**
 * Clamped, and here that is simply what the domain is.
 *
 * Rotation clamps too, but has to say "not wrapped" because its domain is a loop whose ends are
 * the same point. Tilt's ends are real ends — nothing is steeper than vertical — so a scenario
 * that would overshoot sits at the end because there is nowhere further to go.
 */
function clampTilt(v: number): number {
  return Math.min(TILT_MAX, Math.max(TILT_MIN, v));
}

/** Always offered: neither is derived from the answer setup, so neither can become
 *  unrepresentable the way the old value scenarios could.
 *
 *  No Reading sits LAST, as it does on GPS and Compass. It is the absence of a reading rather than
 *  a kind of one, so it reads as the end of the list; Tilt had it first purely by declaration
 *  order, which put the three sensor blocks' menus in two different shapes. */
function tiltScenarioIds(): TiltScenarioId[] {
  return ['offAxis', 'noReading'];
}

/** Whether an armed fault leaves anything for the manual box to hold. An off-axis hold still
 *  produces a number — that is the point of it — so only No Reading takes the value away. */
function tiltFaultSuppressesValue(id: TiltScenarioId): boolean {
  return id === 'noReading';
}

/**
 * The simulated reading for an armed fault.
 *
 * Only two outcomes now: nothing at all, or the value the teacher typed (falling back to an authored
 * answer so the faults stay usable before anything has been typed). The randomised, answer-derived
 * draws are gone with the value scenarios, and with them the reason this needed a nonce.
 */
function tiltSimReading(
  id: TiltScenarioId, d: TiltBlockEditorData, manual: number | null,
  resolve?: (s: string, fieldId?: string) => number,
): number | null {
  if (tiltFaultSuppressesValue(id)) return null;
  if (manual != null) return clampTilt(manual);
  return tiltSimBases(d, resolve)[0]?.center ?? null;
}

interface TiltTestData {
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  hasChips: boolean;
}

/**
 * A band as text, with the whole domain named rather than described.
 *
 * `0° to 360°` is the complement of an answer key that accepts nothing, and printing the bounds
 * makes it look like a configured range a student could miss. Compass has always collapsed the
 * equivalent band to "Any heading" (formatCardinalRange); Angle spelled the numbers out.
 */

/**
 * Fans one feedback state out into one legend pill per range — the same breakdown the feedback panel
 * renders, just as TestBarChips. Follows MCQ's convention for a multi-part condition: each part is its
 * own pill, because a single joined label hides half of what the state actually covers.
 *
 * No connectors between them: a legend chip lights up when its state fires, which already tells the
 * teacher which range was hit, and the rail is far too narrow to spend a row on the word "or".
 */
/**
 * ANGLE'S ONE DERIVATION of what a default state's condition looks like, in either mode.
 *
 * Angle answered this in five places split by mode and surface — `correctFan`/`incorrectFan` for
 * the legend, `summarizeCorrect` for the answer bar, `incorrectFormulaChip` for the panel when
 * unrolled, the per-gap chips when rolled — plus two lib label functions. Compass had the same
 * six-way split and the same consequence. See `compassChips` for the full reasoning; this is its
 * twin, differing only in that an angle has one-sided condition types (`< {n}`) and so carries a
 * comparison symbol on its unrolled reading.
 */

/** `resolve` turns the editor's raw formula text ("{n}*30") into the number the maths needs.
 * Defaults to `num`, so every existing caller — and any plain-number setup — behaves exactly as
 * before. Tolerances deliberately never resolve: a tolerance on a loop is absolute degrees, not an
 * equation. Same bridge shape slider and numerical already use. */
/** Angle's sensor states, in the order `evaluateTilt` gates them. Declared once because the
 *  editor hook and the non-editing render paths both seed them, and a block whose panel and test
 *  legend disagree about what exists is worse than one with neither. */
const TILT_SENSOR_DEFAULTS = (t: (key: string, opts?: Record<string, unknown>) => string) => [
  {
    trigger: NO_SENSOR_DATA_TRIGGER,
    title: t('blocks.sensors.noSensorDataFeedback'),
    content: `<p>${t('blocks.tilt.noSensorDataDefault')}</p>`,
    evaluation: 'neutral' as const,
  },
  {
    trigger: 'poor_axis',
    title: t('blocks.tilt.poorAxisFeedback'),
    content: `<p>${t('blocks.tilt.poorAxisDefault')}</p>`,
    evaluation: 'neutral' as const,
  },
];

/** Exported for `angle-mode-grading`, which grades through the REAL chain rather than a
 *  stand-in: the point of that test is that the app's own wiring resolves per field. */
export function tiltBlockDataForEval(d: TiltBlockEditorData, resolve: (s: string, fieldId?: string) => number = num): TiltBlockData {
  return {
    question: d.question,
    visibilityMode: d.visibilityMode,
    conditionType: d.conditionType,
    answers: d.answers.map(a => ({ id: a.id, value: resolve(a.value, `${a.id}.value`), toleranceValue: num(a.toleranceValue), toleranceAbove: num(a.toleranceAbove), toleranceBelow: num(a.toleranceBelow) })),
    betweenAnswers: d.betweenAnswers.map(a => ({ id: a.id, low: resolve(a.low, a.id), high: resolve(a.high, a.id) })),
    toleranceSymmetry: d.toleranceSymmetry,
    threshold: resolve(d.threshold, 'threshold'),
    bypassEnabled: d.bypassEnabled,
    gradingMode: d.gradingMode,
    feedback: d.feedback,
  };
}

/** Whether the ANSWER holds an equation — see the compass twin. The threshold counts: under
 * lt/lte/gt/gte it IS the answer. */
function tiltAnswerHasFormula(d: TiltBlockEditorData): boolean {
  return d.conditionType === 'exact' ? d.answers.some(a => hasVariableChip(a.value))
    : d.conditionType === 'between' ? d.betweenAnswers.some(a => hasVariableChip(a.low) || hasVariableChip(a.high))
    : hasVariableChip(d.threshold);
}

function computeTiltTestData(
  d: TiltBlockEditorData,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale: string,
  reading: number | null,
  hasSubmitted: boolean,
  /** A typed manual-bypass angle, already normalized, or null. Takes precedence over `reading`: a
   * student only reaches for the bypass when the sensor has failed them, so if they have typed an
   * answer that is the answer, whatever the instrument is still claiming. */
  bypassAnswer: number | null = null,
  /** Resolves a formula answer against the roll being tested. Omitted (or `num`) for a caller with
   * no roll in hand, which is exactly right for a plain-number setup. */
  resolve: (s: string, fieldId?: string) => number = num,
  /** The roll the TEST is running against — legend chips resolve, unlike feedback chips. */
  rolledValues: Record<string, string> = {},
  /**
   * The instrument produced nothing — no samples, or the simulator's `noReading`.
   *
   * Passed rather than inferred from a null `reading`, because those are different facts: a null
   * reading is also what a student who has not moved the phone yet has, and that one is an
   * ordinary No Answer.
   */
  sensorDead = false,
  /** How far the gesture drifted off its calibration plane, when the tracker measured it. */
  offAxisDegrees: number | null = null,
): TiltTestData {
  const evalData = tiltBlockDataForEval(d, resolve);
  // `bypassAvailable` decides whose fault a dead sensor is, so it is read here and not stored:
  // `unlockBypass` is a Flow effect, and the answer can differ between two attempts.
  const health: SensorHealth = sensorDead
    ? { fault: 'noData', bypassAvailable: d.bypassEnabled }
    : offAxisDegrees != null ? { offAxisDegrees }
    : {};
  // `method` is analytics metadata here rather than a gate — unlike evaluateCompass, evaluateTilt
  // has no accuracy checks to skip, since an Angle reading carries no confidence figure at all.
  const testResult = !hasSubmitted ? null
    : bypassAnswer != null
      // A typed bypass has no instrument behind it, so there is no health to report — the same
      // reason compass passes an unreported heading accuracy for its own bypass.
      // A typed bypass has no instrument behind it and no health to report — the student answered.
      ? evaluateTilt({ touched: true, tiltDegrees: bypassAnswer, method: 'manual' }, {}, evalData)
      : evaluateTilt({ touched: reading != null, tiltDegrees: reading ?? 0, method: 'sensor' }, health, evalData);
  const firedIds = new Set((testResult ?? []).map(s => s.id));
  const states = d.feedback.states;
  const find = (trigger: string) => states.find(s => s.type === 'default' && s.trigger === trigger);

  const chips: TestBarChip[] = [];
  const push = (s: FeedbackState | undefined, category: TestBarChip['category'], condition: React.ReactNode, reachable?: boolean) => {
    if (!s) return;
    chips.push({ id: s.id, condition, evaluation: s.evaluation, active: firedIds.has(s.id), category, reachable });
  };

  /**
   * The participation arms used to emit the catch-all and RETURN, which put the early exit above
   * the targeted loop below — fine while a value block could not fire a targeted state outside
   * graded, wrong the moment it could. The mode now decides what to SKIP, not where to stop.
   */
  const participation = d.gradingMode !== 'graded';

  if (d.gradingMode !== 'optional') {
    push(find('unanswered'), 'unanswered',
      stateChipLabel({ id: '', type: 'tilt', data: d } as never, { type: 'default', trigger: 'unanswered' }, t, locale));
  }

  // A block with no usable answer yet has NO accepted band — not the degenerate one a blank field
  // parses into. The feedback panel has always read it that way: its Correct card is gated on
  // `isTiltSetupIncomplete` and its Default Incorrect gaps come out as the whole domain. The
  // test bar built a band around zero instead, so a freshly added block advertised "0° to 0°" as
  // the correct answer and called the catch-all unreachable — precisely inverting the panel beside
  // it. Same helper on both sides now.
  const setupDead = isTiltSetupIncomplete(d);
  const acceptedRanges = setupDead ? [] : computeTiltAcceptedRanges(tiltAcceptedRangeConfig(d, resolve));
  // Nothing is correct outside graded, so the bar has no Correct chip to draw.
  const correctState = participation ? undefined : find('correct');
  // Hoisted: both default states derive from one function that needs the carve-outs.
  const targetedTriggers = states.filter(s => s.type === 'targeted').map(s => s.trigger);
  /** An EMPTY map means "not rolled yet", not "no variables" — passing it as a roll would resolve
   *  every formula to NaN. */
  const chipRoll = Object.keys(rolledValues).length > 0 ? rolledValues : null;
  /** The angle actually submitted, keyed for `activeIntervalIds` — so the legend lights the ONE
   *  band or gap it fell in rather than every chip of the state. Scope '' because an angle has one
   *  number line, unlike slider's two handles. */
  const submittedByScope: Record<string, number | null> = { '': bypassAnswer ?? reading ?? null };
  const barEnvelope = (st: FeedbackState, category: 'correct' | 'incorrect', list: ConditionChips) => ({
    id: st.id, category, active: firedIds.has(st.id),
    activeSourceIds: activeIntervalIds(list, submittedByScope),
    unreachableLabel: t('blocks.feedbackPanel.unreachable'),
    connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
  });
  // The ONE derivation, in the legend's envelope. The joined-label fallback is gone: it existed
  // only because the per-range fan-out could not describe an unrolled equation, which is the mode
  // fork `tiltChips` now owns. Setup-incomplete stays its own case — there is no answer at all.
  // Derived ONCE and reused for the list and the highlight.
  const correctList = correctState ? tiltChips(d, correctState, chipRoll, t, locale, targetedTriggers) : [];
  const correctFan = (setupDead || !correctState) ? []
    : toTestBarChips(correctList, barEnvelope(correctState, 'correct', correctList));
  if (setupDead) push(correctState, 'correct', t('blocks.feedbackPanel.unreachable'), false);
  else chips.push(...correctFan);

  for (const s of visibleTargeted(states, d.gradingMode)) {
    /*
     * Set Answer holds no value of its own — it IS the authored answer — so running it through
     * the trigger chip produced "< ?°" on the collapsed card, a condition with its operand
     * missing. The preset therefore states the authored condition, which is the same chips the
     * feedback panel shows and the same ones graded's Correct row draws. Ported from Compass,
     * whose own note is the one this follows: never "= ?" from a trigger that holds no value.
     */
    if (s.trigger === SET_ANSWER_TRIGGER) {
      const presetList = neutralChips(tiltChips(d, { trigger: 'correct' } as never, chipRoll, t, locale, targetedTriggers));
      chips.push(...toTestBarChips(presetList, {
        id: s.id, category: 'targeted', active: firedIds.has(s.id),
        activeSourceIds: activeIntervalIds(presetList, submittedByScope),
        unreachableLabel: t('blocks.feedbackPanel.unreachable'),
        connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
      }));
      continue;
    }
    chips.push({
      id: s.id,
      condition: resolvedTriggerChip({
        trigger: s.trigger, scenario: rolledValues,
        summaryOf: resolved => tiltTargetedChipLabel(resolved, d, t, locale),
        evaluate: (expr, values) =>
          evaluateFormulaRaw(expr, values, angleModeFor(d, `feedback.${s.id}.trigger`)),
        valuesOf: triggerValuesOf,
        hasChip: hasVariableChip,
        resolveTrigger: resolveTriggerValues,
      }),
      evaluation: displayEvaluation(d.gradingMode, s.evaluation),
      active: firedIds.has(s.id),
      category: 'targeted',
    });
  }


  if (participation) {
    const catchAll = find(d.gradingMode === 'optional' ? 'any_response' : 'any_answer');
    if (catchAll) {
      /*
       * OPTIONAL is ONE ROW. Everything accumulated above — the gates, the targeted loop — belongs
       * to a mode that can fire them, and optional fires none of them: it is inert, so Any Response
       * is the only outcome it has. Cleared rather than never-pushed, because the rungs above are
       * shared with survey, which does fire them. `legendStatesFor` makes the same call for the
       * blocks that build their legend through it.
       */
      if (d.gradingMode === 'optional') chips.length = 0;

      /*
       * The SAME derivation the panel's catch-all uses, so the bar and the card cannot disagree
       * about what still falls through. It enumerates once Set Answer carves the accepted band
       * out, and says the plain label when nothing has. The tick goes the moment a target exists.
       *
       * Tilt had hand-rolled a plain label in every case, on the stated ground that an open domain
       * has nothing to enumerate. That reasoning came from Rotation's 0-360 loop and does not hold
       * here: once a target owns -50 to -40, what the catch-all gets is -90 to -50 and -40 to 90,
       * which is exactly the thing the value blocks are explicit about. The result was a bar that
       * said "Any Tilt Accepted" beside a targeted chip that had plainly taken part of the domain
       * away from it.
       */
      const targetedList = visibleTargeted(states, d.gradingMode);
      chips.push(...toTestBarChips(
        catchAllChips(d.gradingMode, targetedList.length > 0, t,
          // Enumerates whenever ANYTHING is targeted; `acceptedClaimed` only decides whether the
          // band comes out of the leftovers, which it does once a Set Answer target owns it.
          () => tiltChips(d, { trigger: 'incorrect' } as never, chipRoll, t, locale,
            targetedTriggers, targetedList.some(x => x.trigger === SET_ANSWER_TRIGGER))),
        {
          id: catchAll.id, category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
          active: firedIds.has(catchAll.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
    return { testResult, testChips: chips, hasChips: chips.length > 0 };
  }

  const incorrectState = find('incorrect');
  // See the panel's own incorrectFormulaChip: an unrolled equation gives the coverage maths nothing
  // real, and it concluded "fully covered" for a state that catches every reading.
  const unresolvedFormulaAnswer = tiltAnswerHasFormula(d) && correctFan.length === 0 && !setupDead;
  const incorrectReachable = unresolvedFormulaAnswer || !isTiltDefaultIncorrectUnreachable(acceptedRanges, targetedTriggers);
  // The same gaps the feedback panel lists, so the two panels stop describing Default Incorrect
  // differently — the legend used to say "Anything but 29 to 31" while the feedback card listed the
  // real leftovers, "0 to 29 or 31 to 360".
  const incorrectList = incorrectState ? tiltChips(d, incorrectState, chipRoll, t, locale, targetedTriggers) : [];
  const incorrectFan = (incorrectReachable && incorrectState)
    ? toTestBarChips(incorrectList, barEnvelope(incorrectState, 'incorrect', incorrectList))
    : [];
  if (incorrectFan.length > 0) chips.push(...incorrectFan);
  else push(incorrectState, 'incorrect', null, incorrectReachable);

  return { testResult, testChips: chips, hasChips: chips.length > 0 };
}

function firedFeedbackEvalClass(evaluation: FeedbackState['evaluation']): string {
  return chipToneClass(evaluation);
}
/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderTiltFiredFeedback(testResult: FeedbackState[] | null): React.ReactNode {
  return <FiredFeedback states={testResult} className='mt-3 flex flex-col gap-2' />;
}

/**
 * How far the phone may twist off the plane it started in before the reading is called into
 * question. Past this, gravity is leaning far enough along the axis the measure is NOT
 * reading that the phone is held across the feature rather than along it.
 *
 * Eight degrees is deliberately tighter than the point where the number goes visibly wrong (a 10
 * degree lean costs only about 0.4 degrees of angle), so the warning lands before the reading does.
 */
// The live warning and the grading gate read the SAME number, from the evaluator that owns it.
// Two copies of one threshold is how a student gets warned at 8 and graded at something else.

/**
 * `min-w` matters as much as the type here. This readout shares a `flex-wrap` row with Capture, so its WIDTH decides whether that row fits on one line — and a placeholder two characters
 * wide growing into a six-character reading was enough to rewrap the row and shove everything below
 * it down the page. Reserving the widest reading ("359.9°", six characters, and monospace with
 * tabular figures so a character is a fixed width) keeps the row's shape constant whatever it shows.
 */
const TILT_READOUT_CLASS = 'inline-block min-w-[6ch] select-none text-center font-mono text-5xl tabular-nums';

/**
 * The instrument's presentation state.
 *
 * Every string arrives pre-translated. The block computes them because deciding which one applies
 * needs the live reading and the sensor status, neither of which the display component sees —
 * and threading both down just so it could call `t` itself would put measurement logic inside a
 * component whose whole job is to be the student surface, unmodified, on three different screens.
 */
interface TiltAdvisoryCopy {
  /** Names the condition, carrying its own punctuation. Deliberately reuses the words the simulate
   * dropdown uses for the same fault, so what a teacher arms and what a student reads are one
   * vocabulary — the same tie GPS and Compass make between their scenario, advisory and feedback
   * wording. */
  label: string;
  advice: string;
}

/**
 * What the student is told about the instrument. No calibration, and that is the shape of the
 * whole block.
 *
 * Rotation needs one because it integrates a PATH: without a known starting attitude there is
 * nothing for a swept angle to be measured FROM, so Calibrate is a prerequisite and Capture is
 * gated on it. A pose is measured from gravity, which is already absolute — every sample is
 * independently the whole answer. So there is no reference to set, no window to expire, no
 * recalibrate prompt, no refusal to report, and Capture is available the moment there is a
 * reading.
 */
interface TiltSensorUi {
  /** Why the block is on the simulator rather than the sensor. A plain device statement with no
   * separable condition to name, so no label. */
  notice: string | null;
  /** What the instrument is doing right now. Not a warning. */
  hint: string | null;
  /** The phone is held across the feature, so the number is about the wrong axis. */
  warning: TiltAdvisoryCopy | null;
}

/**
 * THE student surface: exactly what a student sees and does, and nothing else.
 *
 * Same contract as `GpsAnswerDisplay`/`CompassAnswerDisplay` — the live-test card, Preview and the (not
 * yet built) student runtime all render THIS, unmodified, so anything added here reaches students and
 * anything a student must never see belongs in `TiltTestWidget`. Order is the student's reading order:
 * question, then the readout and its capture control, then whatever feedback fired.
 */
function TiltAnswerDisplay({
  d, reading, hasReading, liveReading, onTakeReading, onClearReading, t, locale, firedFeedback, disabled, bypass, sensorUi, scenario,
}: {
  d: TiltBlockEditorData;
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`'s own note. */
  scenario?: React.ReactNode;
  /** The CAPTURED answer — what gets evaluated. Null until Capture is pressed. */
  reading: number | null;
  hasReading: boolean;
  /** The angle being read RIGHT NOW, independent of any capture.
   *
   * The real gravity sensor when the device has one, the teacher's armed simulator scenario
   * otherwise. Both arrive here as a plain number, so this component cannot tell them apart and
   * does not need to.
   *
   * Shown even though Capture is what commits: a student holding a phone against a rock cannot
   * know they have reached 47 degrees if the readout stays blank until they press, so they would
   * be committing blind. The live number is also the only way to notice the phone is held across
   * the feature rather than along it.
   *
   * Absolute, with no reference to drift from. Rotation's equivalent is relative to a calibration
   * and has to say so; gravity is its own reference. */
  liveReading?: number | null;
  onTakeReading?: () => void;
  /** Discards the committed reading and puts the live readout back — see SensorCaptureButton. */
  onClearReading?: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  firedFeedback?: React.ReactNode;
  /** Non-interactive summary for the unselected canvas card: same markup, nothing clickable. */
  disabled?: boolean;
  /** The student's typed fallback answer. Absent only on the unselected card, which renders the
   * box as an inert span (a real input cannot live inside the card's activation button).
   * `answer` is the same value parsed and normalized, or null — it drives the READOUT, not just the
   * evaluation, so a student who types their answer sees it where the sensor's would have been. */
  bypass?: { value: string; onChange: (v: string) => void; invalid: boolean; answer: number | null };
  /** The instrument's presentation state. Absent on the unselected card, which is inert.
   *
   * Capture is never gated here — there is no prerequisite to wait for. Rotation gates it on a
   * calibration because an uncalibrated sweep past 180 silently mirrors; a pose has nothing to
   * mirror and no reference to establish first. */
  sensorUi?: TiltSensorUi;
}) {
  // `exact` only: a tolerance around a hidden angle discloses precision, while a `between` range or
  // a threshold IS the answer. Absolute always — Angle hardcodes `toleranceMode: 'absolute'`, since a
  // percentage of an angle is meaningless on a loop.
  const { i18n } = useTranslation();
  const firstAnswer = d.answers[0];
  const sameTolerance = d.answers.every(a =>
    a.toleranceValue === firstAnswer?.toleranceValue && a.toleranceAbove === firstAnswer?.toleranceAbove
    && a.toleranceBelow === firstAnswer?.toleranceBelow);
  // See `effectiveSetting`: a mode that takes the setting away must also take its effect away.
  const toleranceHintText = effectiveSetting('showTolerance', d.gradingMode, d.showTolerance)
    && d.conditionType === 'exact' && firstAnswer && sameTolerance
    ? toleranceHint(
        { symmetry: answerSymmetry(firstAnswer, d), toleranceValue: firstAnswer.toleranceValue,
          toleranceAbove: firstAnswer.toleranceAbove, toleranceBelow: firstAnswer.toleranceBelow },
        { fmt: (n: number) => fmtLocale(n, i18n.language), t, unit: '°' })
    : null;
  const bypassAnswer = bypass?.answer ?? null;
  /** Whether what is on screen is the student's ANSWER rather than the instrument still reading. */
  const captured = bypassAnswer != null || hasReading;
  /** The one number on screen, whatever its provenance — read once so the readout and the
   *  announcement can never disagree about what was committed. */
  const shownDegrees = bypassAnswer != null ? bypassAnswer
    : hasReading && reading != null ? reading
    : liveReading ?? null;
  return (
    <div
      className={`flex flex-col gap-3 ${disabled ? 'pointer-events-none' : ''}`}
      aria-hidden={disabled || undefined}
    >
      {scenario}
      <BlockQuestion html={d.question} />

      {/* GUIDANCE, so it sits with the instruction and above the instrument, never after a reading.
          Same placement and italic treatment as GPS and Compass. */}
      {toleranceHintText && <p className={`text-center ${TOLERANCE_HINT_CLASS}`}>{toleranceHintText}</p>}

      {/* Everything below the question dims on the collapsed card: the reading shown there is
          representative, not a live fix. The question is NOT dimmed — it is the same authored
          text on every surface, and dimming it also faded its variable chips to 50% in these
          three blocks and no others. */}
      {/*
        * THE SAME COLUMN THE OTHER BLOCKS GET, which this one was never given.
        *
        * Seven blocks route their answer through `AnswerDisplayFrame`, which caps and centres
        * it; the three sensors and FITB hand-roll the frame's shape and so were full-width and
        * left-aligned. The instrument itself was centred by its own `mx-auto w-fit` and looked
        * fine, which is why only the FEEDBACK under it read as out of place — it was the one
        * part wide enough to show the difference.
        *
        * The class and not the frame, because the frame also opens a `BlockPreviewProvider`,
        * and Preview deliberately supplies one roll for the whole page — see this block's own
        * note on not nesting it. `ordering-block` applies the column the same way and for the
        * same reason.
        */}
      <div className={`${ANSWER_COLUMN_CLASS} flex flex-col gap-3 ${disabled ? 'opacity-50' : ''}`}>

        <div className='mx-auto flex w-fit flex-col items-center gap-2'>
          {/* Capture ABOVE the readout, matching GPS and Compass: the control is the cause and the
              number is its effect, so top-to-bottom reads in that order. Tilt had the two side by
              side, inherited wholesale from Rotation's row — where there were genuinely two controls
              (Calibrate, then Capture) competing for the line. Tilt has one, so the row bought
              nothing and cost the agreement with the other two sensor blocks. The `flex-wrap` that
              row needed goes with it. */}
          <SensorCaptureButton
            committed={captured}
            captureLabel={t('blocks.tilt.captureButton')}
            icon={<TiltIcon />}
            onCapture={onTakeReading}
            onReset={onClearReading}
            disabled={disabled}
          />
          {/*
            Priority: a typed bypass, then the captured answer, then the live instrument value. A
            bypass wins over the sensor here exactly as it does in the evaluation — showing the
            instrument's angle while grading the typed one would tell a student their answer is
            something other than what they entered.

            Frozen once captured rather than left updating: after committing, the student needs to
            see WHAT they committed, not the angle their hand has drifted to since. Reset is how
            they get the live view back.
          */}
          <span className={`${TILT_READOUT_CLASS} ${sensorReadoutTone(captured)}`}>
            {shownDegrees != null ? `${fmtDegrees(shownDegrees, locale)}°`
              : <SpokenPlaceholder>{t('blocks.tilt.liveReadPlaceholder')}</SpokenPlaceholder>}
          </span>
          {/* The announcement, and the only live region here. The readout above used to carry
              `aria-live` itself, which announced the instrument once per sample while it was still
              merely live — see SensorAnnounce. */}
          <SensorAnnounce
            value={hasReading && shownDegrees != null ? `${fmtDegrees(shownDegrees, locale)}°` : null}
          />
          {/* One line at a time, most urgent first, so the reading never carries a stack of advice:
              a refusal first (the student did something the sensor rejected), then a live warning,
              then commentary on a healthy reading. The sensor notice sits outside that ladder
              because it explains the READOUT's provenance rather than the reading's state, and a
              teacher on a computer needs it alongside whatever else is showing.

              Anything WRONG goes through SensorAdvisory, so a Tilt problem carries the same triangle
              and the same warning colour as GPS's and Compass's. */}
          {sensorUi?.warning ? (
            <SensorAdvisory label={sensorUi.warning.label} advice={sensorUi.warning.advice} />
          ) : null}
          {/* Live tracking commentary, a separate register from the instruction above: that one says
              what to do, this says what the sensor is currently seeing. Device mode only. */}
          {sensorUi?.hint && !sensorUi.warning && (
            <p className='text-center text-xs text-ws-muted'>{sensorUi.hint}</p>
          )}
          {sensorUi?.notice && <SensorAdvisory advice={sensorUi.notice} />}
          {/* The manual-entry fallback a teacher can enable, shown on EVERY surface when enabled — not
              just the unselected card. Gating it on `disabled` meant a teacher who ticked the setting saw
              no trace of it in test or Preview, which is the divergence this component exists to prevent.

              A REAL input, ported from Compass. It was a static span placeholder deferred to "the
              student runner", which left the one affordance for a student whose sensor doesn't work as
              the one thing a teacher could not try. Angle needs it at least as much as Compass does:
              its reading is a MOTION requiring a gyroscope, so any device without one, or any student
              who cannot perform the sweep, has no other way to answer. On the unselected card it
              degrades to a span, for the same reason the capture button does. */}
          {d.bypassEnabled && (
            <SensorManualBypass
              label={t('blocks.manualBypass.label')}
              quantityLabel={t('blocks.tilt.manualBypassQuantity')}
              value={bypass?.value ?? ''}
              onChange={bypass?.onChange ?? (() => {})}
              invalid={bypass?.invalid ?? false}
              invalidMessage={t('blocks.manualBypass.outOfRange')}
              inert={!bypass}
            />
          )}
        </div>

        {firedFeedback}
      </div>
    </div>
  );
}

/** The teacher's test harness wrapped around `TiltAnswerDisplay` — see CompassTestWidget's own doc
 * comment. The Simulate picker is teacher-only and must stay out of the answer display; it also shows
 * in Preview, because Angle has no sensor capture implemented at all (the tilt-gesture capture is still
 * a design), so simulation is currently the only way to read this block anywhere. */
function TiltTestWidget({
  d, scenarioId, onScenarioChange, mode, onModeChange, deviceStatusHint, reading, hintReading, liveReading, manual, onTakeReading, onClearReading, hasReading, t, locale, bypass, sensorUi, children, sharedDevice, scenario
}: {
  /** The leader's variant, ghosted, above the question — forwarded to the display. */
  scenario?: React.ReactNode;
  d: TiltBlockEditorData;
  /** `null` when no fault is armed — the normal case, where the reading is whatever is typed. */
  scenarioId: TiltScenarioId | null;
  onScenarioChange: (id: TiltScenarioId | null) => void;
  reading: number | null;
  /** Ungated: what the armed selection WOULD report, for the always-visible hint. Angle withholds
   * every reading until Capture, so a hint gated on `reading` was blank exactly when the teacher was
   * choosing a scenario — the choice looked like it did nothing. Same fix Compass and GPS got. */
  hintReading: number | null;
  /** What the instrument reads right now, shown continuously so the sweep can be aimed. */
  liveReading: number | null;
  /** Typed angle for the simulate strip, the escape hatch a fixed preset list cannot cover — and the
   * only way to drive this block at all on a machine with no motion sensors. */
  manual: { value: string; onChange: (v: string) => void; invalid: boolean };
  onTakeReading: () => void;
  onClearReading: () => void;
  hasReading: boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
  locale: string;
  bypass: { value: string; onChange: (v: string) => void; invalid: boolean; answer: number | null };
  sensorUi: TiltSensorUi;
  mode: SensorSourceMode;
  onModeChange: (next: SensorSourceMode) => void;
  /** What the motion sensor is doing, or why it cannot be read here. Only consulted in device mode. */
  deviceStatusHint: string;
  /**
   * Preview drives this block from the task's ONE phone, so the per-block strip comes out: three
   * strips in Preview model three phones, which is the thing the shared simulator exists to stop.
   * The strip stays in the EDITOR, where a teacher is testing one block against its own answer in
   * isolation and that is exactly what they want.
   */
  sharedDevice?: boolean;
  children?: React.ReactNode;
}) {
  const setupIncomplete = isTiltSetupIncomplete(d);
  // An existing reading is described first, ahead of the incomplete-setup notice — a typed angle
  // produces one with no answer authored, and telling a teacher to "set an answer" while the strip
  // is showing them their own value contradicts what is on screen.
  const hint = mode === 'device' ? deviceStatusHint
    : hintReading != null
    ? t('blocks.tilt.simulateWillRead', { value: `${fmtDegrees(hintReading, locale)}°` })
    : scenarioId != null ? t(`blocks.tilt.simulateFaultHint.${scenarioId}`)
    : setupIncomplete ? t('blocks.tilt.simulateNeedsAnswer')
    : '';
  return (
    <div className='flex flex-col gap-3'>
      {!sharedDevice && (
      /* The same strip Compass and GPS use. The Device tab reads the real gravity sensor through
         `useTiltSensor`; before it existed, a live sensor simply overrode the simulator, which left
         a teacher authoring on a phone with no way to reach these faults at all. */
      <SensorSimulateBar
        groupLabel={t('blocks.sensorSource.scenarioGroup')}
        scenarios={tiltScenarioIds().map((id): SensorScenarioOption => ({
          id,
          label: t(`blocks.tilt.simulateScenario.${id}`),
          suppressesValue: tiltFaultSuppressesValue(id),
        }))}
        activeScenarioId={scenarioId}
        onPickScenario={id => onScenarioChange(id === '' ? null : (id as TiltScenarioId))}
        // Never gated on the answer setup any more: every remaining option is a fault that needs
        // nothing from it, and the manual box is a literal.
        mode={mode}
        onModeChange={onModeChange}
        deviceTabLabel={t('blocks.sensorSource.device')}
        simulateTabLabel={t('blocks.sensorSource.simulate')}
        manual={{
          label: t('blocks.tilt.simulateManualLabel'),
          active: manual.value.trim() !== '' && !manual.invalid,
          fields: [{
            key: 'tilt',
            disabled: scenarioId != null && tiltFaultSuppressesValue(scenarioId),
            ariaLabel: t('blocks.tilt.simulateManualAriaLabel'),
            value: manual.value,
            onChange: manual.onChange,
            invalid: manual.invalid,
            suffix: '°',
          }],
        }}
        hint={hint}
      />
      )}

      <TiltAnswerDisplay
          scenario={scenario}
        d={d}
        reading={reading}
        hasReading={hasReading}
        onTakeReading={onTakeReading}
        onClearReading={onClearReading}
        liveReading={liveReading}
        t={t}
        locale={locale}
        bypass={bypass}
        sensorUi={sensorUi}
        firedFeedback={children}
      />
    </div>
  );
}

function tiltCollapsedBar(
  d: TiltBlockEditorData,
  /** Nodes, not a string: a formula answer renders through `FormulaWithValues` so the variable
   * appears as a CHIP, the same as numerical's and slider's answer bands. A plain string would put
   * the literal characters "{angle}" on the card. */
  correctSummary: React.ReactNode,
  t: (k: string, opts?: Record<string, unknown>) => string,
  issues: BlockIssue[],
): React.ReactNode {
  // Informational-only, no enforcement — angle is equally a phone-sensor block, shown on
  // every state of this bar for consistency with GPS's own convention.
  const mobileChip = <MobileOnlyChip />;

  // The mobile chip rides along in every state, which used to be the stated reason this bar
  // couldn't call a shared renderer. It can now: `trailing` is part of the shared bar's own
  // contract, so all three sensor blocks and the six others render one implementation.
  const issueBar = collapsedIssueBar(issues, mobileChip);
  if (issueBar) return issueBar;

  if (d.gradingMode !== 'graded') return collapsedModeBar(d.gradingMode, t, 'tilt', mobileChip);

  // Raw-answer style (matches numerical/GPS's collapsed bar), not a chip/pill.
  return (
    <CollapsedBlockBar tone='success' align='start' scrollX trailing={mobileChip}>
      <span className='min-w-0'>{correctSummary}</span>
    </CollapsedBlockBar>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TiltBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename,
}: TiltBlockProps) {
  const { t, i18n } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level answer is vestigial once a bank exists. */
  /**
   * FOLLOWING A SCENARIO — the value family's shape, as Numerical and Slider carry it. A row
   * holds `TILT_VERSION_KEYS` flat, so the editor resolves one exactly as it resolves a version and
   * `applyScenarioRow` is the only extra step.
   */
  const storedForLink = initData(block.data);
  const leaderVersions = ((blocks ?? []).find(b => b.id === storedForLink.followsBlockId)?.data as
    { versions?: DisplayVersion[] } | undefined)?.versions;
  const [leaderShownRaw, setLeaderShown] = useVersionInForce(storedForLink.followsBlockId ?? block.id, 'test');
  const shownScenarioId = leaderShownRaw ?? leaderVersions?.[0]?.id;

  /**
   * WHAT THE TEST BAR PAGES WHEN THIS BLOCK FOLLOWS — the leader's scenarios, not its own bank.
   *
   * Same shape `useScenarioFollower` returns for the blocks that use it, and the same one
   * Dropdown and MCQ already build. A follower has no bank of its own (§2.2 makes the two
   * exclusive), so its test bar had nothing to page and a teacher could not step the scenarios
   * from the surface they were testing on — the only pager was in Answer Setup. The pointer is
   * shared, so this and the leader's own card move together rather than disagreeing.
   */
  const testPager = storedForLink.followsBlockId
    ? { versions: (leaderVersions ?? []) as { id: string }[], activeId: shownScenarioId,
      onSelect: setLeaderShown, noun: 'scenario' as const }
    : null;
  /** A version FIRST, then the row on top — a follower has no bank, so only one ever applies. */
  const resolveForEditor = (d: TiltBlockEditorData, versionId: string | undefined) =>
    applyScenarioRow(
      applyTiltVersion(d, versionId) as unknown as Record<string, unknown>,
      shownScenarioId, TILT_VERSION_KEYS,
    ) as unknown as TiltBlockEditorData;
  const initial = useRef(resolveForEditor(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];
  const questionHtmlRef = useRef(initial.current.question);
  /**
   * The question HTML the editor is HANDED, mirroring the ref beside it. The ref is the
   * synchronous read the push paths depend on; a ref cannot re-render, so restored HTML needs
   * state to reach the screen. See keyword, where this was first worked out.
   */
  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedConditionType, setConditionType] = useState(initial.current.conditionType);
  const [editedAnswers, setAnswers] = useState(initial.current.answers);
  const [editedToleranceSymmetry, setToleranceSymmetry] = useState(initial.current.toleranceSymmetry);
  const [editedShowTolerance, setShowTolerance] = useState(initial.current.showTolerance);
  const [editedBetweenAnswers, setBetweenAnswers] = useState(initial.current.betweenAnswers);
  const [editedThreshold, setThreshold] = useState(initial.current.threshold);
  // Answer equations wait for the teacher to stop typing before being called malformed — every
  // formula is invalid partway through. Declared here, above the `if (!isEditing)` branch, because
  // that branch returns: a hook after it would not run on the collapsed path. Keyed off the same
  // state `liveD` is assembled from further down, and off `block.data` for the card.
  const [editedVisibilityMode, setVisibilityMode] = useState(initial.current.visibilityMode);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedBypassEnabled, setBypassEnabled] = useState(initial.current.bypassEnabled);
  const [variableTestMode, setVariableTestMode] = useState<'min' | 'max' | 'random' | null>(null);
  // Numerical's model, not slider's: a scenario SEPARATE from the block-test roll. Null means no
  // scenario picked, and the feedback panel then shows the equation as chips rather than resolving
  // it — a display surface shows the variable, not one roll of it. The test bar keeps using the
  // block roll, because that IS the testing-condition line.
  const [feedbackTestValues, setFeedbackTestValues] = useState<Record<string, string> | null>(null);
  const [armedAnswerId, setArmedAnswerId] = useState<string | null>(null);
  const [armedBetweenId, setArmedBetweenId] = useState<string | null>(null);

  // ── Live test state ─────────────────────────────────────────────────────────
  // Same shape as Compass's — see its own comment. Angle has no accuracy gate, so there is no
  // separate accuracy figure to carry alongside the reading.
  const [testScenario, setTestScenario] = useState<TiltScenarioId | null>(null);
  /**
   * Device or simulator, and a hard either/or.
   *
   * Before this existed the sensor simply won whenever it was live, so a teacher authoring on a phone
   * could not reach the simulate presets at all — the one surface where testing a fault matters most.
   */
  const [sourceMode, setSourceMode] = useState<SensorSourceMode>('simulate');
  // Retained only so Reroll still redraws variables and resets the panel; no simulated reading is
  // randomised any more, since every remaining scenario is a fault with nothing to draw.
  const [, setSimNonce] = useState(0);
  /**
   * What this block handed in last time it was on screen.
   *
   * Restored unconditionally, unlike GPS and Compass. Those two have a Live mode whose readout
   * tracks the device, where putting an old reading back would be a lie about where the student
   * is now. Angle has no such mode: its reading only ever exists because someone pressed Capture,
   * so the captured angle IS the answer and belongs back on screen until they capture again.
   */
  const prior = usePriorAnswer<number>();
  const [testTaken, setTestTaken] = useState(prior !== undefined);
  /** The angle frozen by the last Capture. Test state, not block data. */
  const [capturedDegrees, setCapturedDegrees] = useState<number | null>(prior ?? null);
  const [bypassInput, setBypassInput] = useState('');
  /**
   * The real gravity sensor, plus the tracker that turns it into an angle.
   *
   * `useTiltSensor` attaches no listener until `calibrate()` is first called, so mounting this on
   * every Angle block costs nothing until a student or teacher actually starts the gesture.
   */
  /**
   * Preview's one device, if there is one above us. Null in the editor, which is what keeps this
   * block's own simulate strip behaving exactly as it always has.
   */
  const shared = useSensorSimulator();
  const deviceMode = shared ? shared.useDevice : sourceMode === 'device';
  /**
   * The shared device as a GRAVITY STREAM, not as an angle.
   *
   * This is the whole substance of joining the task-level simulator. The per-block strip returned a
   * number and the tracker never ran, so the one thing it exists for — deciding which side of the
   * 180 fold an attitude is on — could not be exercised: typing 250 asserted an overturn where
   * turning the dial past the fold derives one. Off Plane was a label; here it is a turn axis that
   * is genuinely not horizontal, and the tracker notices by itself.
   *
   * `signal` carries what a poor or absent signal means to an accelerometer, which is why this
   * block no longer needs a fault list of its own — see `noiseForSignal`.
   */
  /*
   * PREVIEW's shared attitude only.
   *
   * Keyed on `shared` alone, this fed the sensor a live simulated stream in the EDITOR too — the
   * provider wraps the whole task page, not just the preview surface — so a capture stored the
   * shared attitude's default of zero while the block's own strip advertised something else.
   *
   * Tilt needs far less of the shared attitude than Rotation does. A pose has no start and no
   * interruption to survive, so `endDegrees` — where the dial has the phone — is the whole input,
   * and it is read as a LEAN because that is what this block measures.
   */
  const simulatedGravity = useMemo(() => (isPreview && shared && !shared.useDevice
    ? rollGravityFor(shared.attitude.endDegrees)
    : null), [shared, isPreview]);
  const sensor = useTiltSensor(deviceMode, simulatedGravity);
  const sensorReading = sensor.reading;
  /**
   * Whether the TRACKER is driving, from either source.
   *
   * Was `deviceMode && sensor.status === 'live'`, which is now too narrow: a simulated stream
   * delivers samples the same way, and everything downstream — the calibration state, the
   * advisories, the reading — should run off the tracker whichever source filled it.
   */
  const sensorLive = sensor.status === 'live';
  /**
   * Whether this block still answers for itself when the tracker is not driving.
   *
   * With a shared device above us it does not, and that is the point: the device answers, INCLUDING
   * by answering nothing. A device reporting no signal must leave the block with no reading, not
   * quietly fall through to this block's own typed value — which is what made "one phone for the
   * whole task" worth building.
   */
  /*
   * Which strip is ON SCREEN, which is Preview's shared device or this block's own — not merely
   * whether a provider exists. `!shared` was the same mistaken assumption Compass and GPS made:
   * `SensorSimulatorProvider` wraps the whole task page, not just the preview surface, so in the
   * EDITOR this read false, `liveTiltFor` fell through to a null sensor reading, and a capture
   * stored nothing while the strip's own hint said "will report 47°".
   */
  const ownSimulate = !isPreview;
  /** The armed fault, or null. Only the editor's per-block strip has these; Preview's faults are
   * conditions of the shared device, so the tracker produces them rather than a scenario naming them. */
  const simFault = sensorLive || !ownSimulate ? null : testScenario;
  /**
   * Every way the instrument ends up with nothing to report.
   *
   * Shorter than Rotation's by two whole cases, and the two it loses are the two a calibration
   * created: a refused calibration, and a broken sample stream that dropped one. A pose has
   * neither — a gap in the samples costs this block nothing, because the next sample is as good
   * as the one before it, and there is no reference attitude for a refusal to be about.
   *
   * What is left is the honest one: no reading at all.
   */
  const tiltSensorDead = sensorLive
    ? sensorReading == null
    : testScenario === 'noReading';

  /**
   * Why the block is on the simulator rather than the sensor, when a teacher would otherwise be left
   * wondering whether the readout is real. Deliberately silent in the `idle` case: before anyone has
   * pressed Calibrate there is nothing to explain.
   */
  const deviceStatusHint =
    sensor.status === 'insecure-context' ? t('blocks.tilt.sensorInsecure')
    : sensor.status === 'denied' ? t('blocks.tilt.sensorDenied')
    : sensor.status === 'unsupported' ? t('blocks.tilt.sensorUnsupported')
    : sensorLive ? t('blocks.tilt.sensorLive')
    : t('blocks.tilt.sensorWaiting');
  /** Repeated under the gesture itself, not just on the strip's hint line, because the student
   * surface is the thing being judged and a readout with no provenance is the confusing part. */
  const sensorNotice = deviceMode && !sensorLive && sensor.status !== 'idle' && sensor.status !== 'requesting'
    ? deviceStatusHint
    : null;

  /**
   * The one way to hold the phone wrong: DIAGONALLY.
   *
   * Portrait and landscape are both fine — the measurement takes whichever of the two axes the
   * slope runs along. Held at an angle between them it splits across both and the reading quietly
   * understates, which is why the off-axis component is warned about rather than discarded.
   */
  const offAxisCopy = {
    label: t('blocks.tilt.offAxisLabel'),
    advice: t('blocks.tilt.offAxis'),
  };
  /*
   * An armed No Reading raises NOTHING here, matching GPS and Compass.
   *
   * It used to show a "the stream broke, calibrate again" advisory inherited from Rotation, which
   * was wrong twice over: Tilt has no calibration to return to, and the other two sensor blocks
   * say nothing at all for the same scenario. What a missing reading looks like is the dashed
   * readout, which says it already, and the No Sensor Data card at submit, which is the part a
   * teacher can author. A device that genuinely cannot measure is a different case and still gets
   * its notice, through `sensorNotice` above.
   */
  const trackingWarning: TiltAdvisoryCopy | null = !sensorLive
    ? (simFault === 'offAxis' ? offAxisCopy : null)
    : sensorReading != null && sensorReading.offAxisDegrees > TILT_OFF_AXIS_CUTOFF_DEG
      ? offAxisCopy
      : null;

  /**
   * Commentary on a healthy instrument. One sentence, one state.
   *
   * Rotation needs three states here because a tracked gesture has stages — waiting for motion,
   * tracking, broken. A pose has one: there is a reading, or there is not.
   */
  const trackingHint = sensorLive && sensorReading != null ? t('blocks.tilt.tracking') : null;

  const sensorUiProps = {
    notice: sensorNotice,
    hint: trackingHint,
    warning: trackingWarning,
  };

  /** The instrument's angle, from whichever source filled it. Null when nothing is driving it,
   * which is the editor's own simulate strip — there the scenario and the manual box still answer,
   * and the two tabs stay a genuine either/or rather than a preference. */
  const sensorDegrees = sensorLive ? sensorReading?.degrees ?? null : null;

  /** Scratch text for the simulate strip's manual angle box — applied as typed, never saved. Distinct
   * from `bypassInput`, which is the STUDENT's answer: this one is the teacher driving the simulator,
   * and on a machine with no motion sensors it is the only way to produce a reading at all. */
  const [simManualInput, setSimManualInput] = useState('');
  const [continuous, setContinuous] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  // Already frozen at the moment of Capture, so there is nothing to snapshot at Submit the way
  // GPS and Compass need to -- this is the number the student committed to.
  useReportAnswer(hasSubmitted ? capturedDegrees ?? undefined : undefined);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');
  /** This block's own variable roll, for BLOCK TEST mode only.
   *
   * Preview deliberately does NOT nest this: the task-level BlockPreviewProvider supplies one roll
   * shared by every block on the page, which is the whole point of previewing a task. Testing a
   * single block is the opposite case — its own roll, re-rollable from its own Reroll button. */
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>(() => rollVariableValues(variables));
  // Preview shows ONE task-level roll shared by every block: a task whose blocks all read {n} has
  // to agree about n, and seeding Preview from this block's own roll made them disagree. Block
  // testing keeps its private roll plus reroll, which is the whole point of it.
  const taskPreviewRoll = useTaskPreviewRoll();
  /* Recalled answers ride the same map the variables do — see `useRolledWithRecalls`. The
     equation field, the answer bar and the issue list then all read one roll. */
  const rolledValues = useRolledWithRecalls(isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues);
  // The roll a test grades against. Angles are always degrees, so the formula scope is fixed —
  // unlike numerical, there is no angleMode setting to honour here.
  const resolveAnswer = (s: string, fieldId = ''): number =>
    (s.trim() === '' ? NaN : evaluateFormulaRaw(s, rolledValues, angleModeFor(fields, fieldId)));
  // Fresh values each time the block enters a test surface, so opening a block never shows the
  // stale roll from the last time it was open. Mirrors MCQ's own isSelected/phoneTestMode effects.
  useEffect(() => {
    if (isSelected || phoneTestMode === 'test') setRolledValues(rollVariableValues(variables));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, phoneTestMode]);

  /**
   * The off-axis figure the GRADE is computed against.
   *
   * An armed `offAxis` scenario speaks for itself — it is the teacher asking what a diagonal hold
   * does, and it does not drift. Otherwise this is the captured figure, and before a capture there
   * is the live one, which reaches no gate but keeps the collapsed card's warning honest.
   */
  const gradedOffAxis = () => testScenario === 'offAxis' ? TILT_OFF_AXIS_CUTOFF_DEG + 1
    : testTaken ? capturedOffAxis
    : sensorReading?.offAxisDegrees ?? null;

  /** The angle a capture would commit, from whichever source is driving the readout. */
  function liveTiltFor(data: TiltBlockEditorData): number | null {
    return sensorDegrees ?? (ownSimulate ? simTiltFor(data) : null);
  }
  /**
   * How far off-axis the phone was AT THE MOMENT OF CAPTURE, which is the only moment that counts.
   *
   * The gate used to read this live, straight off `sensorReading`, which defeated it completely:
   * a student could capture a diagonal hold, straighten the phone, and submit a reading taken at
   * 40 degrees off-axis against an off-axis figure of nearly zero. The number being graded was
   * frozen and the health beside it was not, so the two described different instants.
   *
   * It is a sibling of `capturedDegrees` rather than a field on it for the same reason
   * `SensorHealth` is a sibling of the answer: what the device managed is not part of what the
   * student said.
   */
  const [capturedOffAxis, setCapturedOffAxis] = useState<number | null>(null);
  function handleTakeReading(data: TiltBlockEditorData) {
    // Frozen at the moment of capture. The simulator's value is deterministic and could safely be
    // recomputed on render, but a real sensor's cannot: by the next frame the hand has moved, and
    // recomputing would show the student a different number than the one they committed.
    setCapturedDegrees(liveTiltFor(data));
    setCapturedOffAxis(sensorReading?.offAxisDegrees ?? null);
    setTestTaken(true);
    // `continuous` is a TEST-PANEL affordance, so it must not apply in Preview: Preview has no
    // TestBar to switch it off, and a student pressing capture is not submitting — they'd see the
    // answer before the page-level Submit. In Preview only previewSubmitSignal reveals feedback.
    if (continuous && !isPreview) setHasSubmitted(true);
  }
  /**
   * Reset: throw away the committed reading and let the instrument stream again.
   *
   * Clears the ANSWER, not just the display. Keeping the old answer graded while a live number
   * moves on screen would show the student one value and mark another — see SensorCaptureButton.
   * `hasSubmitted` is deliberately left alone: a reset is not an un-submission, and feedback that
   * already fired describes an attempt which genuinely happened.
   */
  function handleClearReading() {
    setCapturedDegrees(null);
    setCapturedOffAxis(null);
    setTestTaken(false);
  }
  /** A typed simulate angle, clamped into the block's own domain. Applied as typed — one numeric box
   * has nothing to parse out of a longer string, so there is no moment where the teacher has to tell
   * us they are finished, and no apply button. */
  const simManualDegrees = parseFloat(simManualInput.replace(',', '.'));
  const simManualValid = simManualInput.trim() !== ''
    && Number.isFinite(simManualDegrees)
    && simManualDegrees >= TILT_MIN && simManualDegrees <= TILT_MAX;
  const simManualProps = {
    value: simManualInput,
    onChange: setSimManualInput,
    invalid: simManualInput.trim() !== '' && !simManualValid,
  };
  /**
   * The reading the simulator would produce.
   *
   * A fault outranks the typed angle now, which is the reverse of the old precedence. It has to: the
   * two faults that suppress a value are the whole reason for arming them, and letting a stale number
   * in the box override "No Reading" would make the fault unreachable without first clearing the box.
   * The box is disabled in exactly those cases, so there is no contradiction on screen.
   */
  function simTiltFor(data: TiltBlockEditorData): number | null {
    if (testScenario != null) {
      return tiltSimReading(testScenario, data, simManualValid ? simManualDegrees : null, resolveAnswer);
    }
    if (simManualValid) return clampTilt(simManualDegrees);
    return null;
  }

  function handleSourceModeChange(next: SensorSourceMode) {
    setSourceMode(next);
    // Nothing to start and nothing to discard. Rotation calibrates on the way in and resets on the
    // way out, because its reading is relative to a reference sample that must not outlive the
    // gesture. A pose is absolute, so switching source changes only where the samples come from.
    if (next === 'device') void sensor.request();
  }

  function handleTestScenarioChange(id: TiltScenarioId | null) {
    setTestScenario(id);
    setSimNonce(n => n + 1);
    if (!continuous) setHasSubmitted(false);
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next && testTaken && !isPreview) setHasSubmitted(true);
  }
  function handleTestSubmit() { setHasSubmitted(true); }
  /** Another draw inside the armed scenario — see sensorSimRandom. */
  /** Another draw inside the armed scenario AND a fresh variable roll — the test panel has one
   * Reroll button and it means "give me a different randomised setup", which covers both. */
  function handleSimReroll() {
    setSimNonce(n => n + 1);
    setRolledValues(rollVariableValues(variables));
  }
  function handleTestReset() {
    setTestTaken(false);
    setHasSubmitted(false);
    // Reset clears what the student COMMITTED. There is no gesture state behind it to unwind —
    // the live reading is whatever gravity says next, captured or not.
    setCapturedDegrees(null);
    // The bypass is the student's answer, so Reset clears it alongside the capture.
    setBypassInput('');
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);

  /** The student's typed bypass answer. Test state, not block data: it is what a STUDENT enters, so
   * it belongs with testTaken/hasSubmitted and is cleared by Reset, never persisted onto the block. */
  const bypassDegrees = parseFloat(bypassInput.replace(',', '.'));
  // Range-checked against the block's own closed domain, NOT wrapped — see the matching note in
  // compass-block.tsx. Typing 985 used to fold to 265 and score a typo as correct.
  const bypassValid = bypassInput.trim() !== ''
    && Number.isFinite(bypassDegrees)
    && bypassDegrees >= TILT_MIN && bypassDegrees <= TILT_MAX;
  /** Typing an answer IS the answering gesture for a student who cannot use the sensor, so it must
   * reveal feedback in continuous test mode exactly as pressing Capture does. Preview is excluded for
   * the same reason the capture handler excludes it: there, only the page-level Submit reveals
   * feedback, and a student typing their answer is not submitting it. */
  function handleBypassChange(v: string) {
    setBypassInput(v);
    if (continuous && !isPreview && v.trim() !== '') setHasSubmitted(true);
  }
  /** Taken exactly as typed. Unlike Compass there is no 360-to-0 fold: Angle's domain is CLOSED, so
   * 360 is a distinct value a teacher can author an answer at, not a synonym for 0. */
  const bypassAnswer = bypassValid ? bypassDegrees : null;
  const bypassProps = { value: bypassInput, onChange: handleBypassChange, invalid: bypassInput.trim() !== '' && !bypassValid, answer: bypassAnswer };

  // Preview's single page-level Submit — reacts to the signal CHANGING, not a per-block click,
  // reusing the same test state the live test widget already drives.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) setHasSubmitted(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  const sharedToolbar = useSharedEditorToolbar();
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  // Every plain <input> in this block focuses through here — see onPlainFieldFocus's own doc
  // comment for why leaving the shared toolbar pointed at the Question editor is a real bug.
  const disconnectSharedToolbar = sharedToolbar.disconnect;
  const questionEditorRef = useRef<Editor | null>(null);

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml,
    visibilityMode: editedVisibilityMode,
    conditionType: editedConditionType,
    answers: editedAnswers,
    toleranceSymmetry: editedToleranceSymmetry,
    showTolerance: editedShowTolerance,
    betweenAnswers: editedBetweenAnswers,
    threshold: editedThreshold,
    gradingMode: editedGradingMode,
    bypassEnabled: editedBypassEnabled,
  };
  const restored = useRestoredData<TiltBlockEditorData>({
    raw: block.data,
    isEditing,
    derive: (raw) => resolveForEditor(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...resolveForEditor(initData(block.data), editorVersion), ...edited } as TiltBlockEditorData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: TiltBlockEditorData) {
      setVisibilityMode(next.visibilityMode);
      setConditionType(next.conditionType);
      setAnswers(next.answers);
      setToleranceSymmetry(next.toleranceSymmetry);
      setShowTolerance(next.showTolerance);
      setBetweenAnswers(next.betweenAnswers);
      setThreshold(next.threshold);
      setGradingMode(next.gradingMode);
      setBypassEnabled(next.bypassEnabled);
      questionHtmlRef.current = next.question;
      setQuestionHtml(next.question);
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(resolveForEditor(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(resolveForEditor(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = () => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump();
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const visibilityMode = isEditing ? editedVisibilityMode : restored.data.visibilityMode;
  const conditionType = isEditing ? editedConditionType : restored.data.conditionType;
  const answers = isEditing ? editedAnswers : restored.data.answers;
  const toleranceSymmetry = isEditing ? editedToleranceSymmetry : restored.data.toleranceSymmetry;
  const showTolerance = isEditing ? editedShowTolerance : restored.data.showTolerance;
  const betweenAnswers = isEditing ? editedBetweenAnswers : restored.data.betweenAnswers;
  const threshold = isEditing ? editedThreshold : restored.data.threshold;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const bypassEnabled = isEditing ? editedBypassEnabled : restored.data.bypassEnabled;

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addTiltVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addTiltVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /** Every field write, routed into the version it belongs to — see mcq-block.tsx's note. */
  const [pendingLink, setPendingLink] = useState<string | null>(null);

  /** Link and unlink — Numerical's, with this block's key set. Every comment there applies. */
  function linkToLeader(followsBlockId: string | undefined, remap?: 'remap' | 'empty') {
    const current = initData(block.data);
    if (followsBlockId && !remap && (current.versions?.length ?? 0) > 1) {
      setPendingLink(followsBlockId);
      return;
    }
    if (!followsBlockId) {
      emit(versionStructure(block.data, {
        ...current, followsBlockId: undefined, scenarioBranches: undefined,
      } as unknown as Record<string, unknown>));
      return;
    }
    const leaderBank = ((blocks ?? []).find(b => b.id === followsBlockId)?.data as
      { versions?: ScenarioLeaderVersion[] } | undefined)?.versions;
    /* Collapse the bank to VERSION 1 first — the block's own top-level copy is whatever
       version 1 said when the bank was made, which is not the same thing. */
    const v1 = current.versions?.[0];
    const base = (current.versions?.length ?? 0) > 1 && v1 ? applyTiltVersion(current, v1.id) : current;
    const baseFeedback = ((current.versions?.length ?? 0) > 1
      ? (v1?.feedback as { states: FeedbackState[] } | undefined) : undefined) ?? feedback;
    const fromBlock = () => ({
      feedback: baseFeedback,
      ...Object.fromEntries(TILT_VERSION_KEYS.map(k => [k, (base as unknown as Record<string, unknown>)[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>);
    const { branches } = remap === 'remap'
      ? remapVersionsToScenarios<FieldVersion<{ states: FeedbackState[] }>, ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.versions,
        v => ({
          feedback: v.feedback,
          ...Object.fromEntries(TILT_VERSION_KEYS.filter(k => k in v).map(k => [k, (v as unknown as Record<string, unknown>)[k]])),
        } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
        fromBlock,
      )
      : syncScenarioBranches<ScenarioFieldBranch<{ states: FeedbackState[] }>>(
        leaderBank, current.scenarioBranches, fromBlock,
      );
    const healed = branches.map((b): ScenarioFieldBranch<{ states: FeedbackState[] }> =>
      (b.feedback?.states?.length ? b : { ...b, feedback: baseFeedback }));
    seedEditorFrom(base);
    resyncFeedbackRef.current?.(baseFeedback);
    emit(versionStructure(block.data, {
      ...base, feedback: baseFeedback, followsBlockId, scenarioBranches: healed, versions: undefined,
    } as unknown as Record<string, unknown>));
  }

  /** The projection, kept live — a variant added to the leader becomes a row here (§4.1). */
  useScenarioRowProjection<ScenarioFieldBranch<{ states: FeedbackState[] }>>({
    followsBlockId: storedForLink.followsBlockId,
    blocks,
    rows: storedForLink.scenarioBranches,
    template: () => ({
      feedback,
      ...Object.fromEntries(TILT_VERSION_KEYS.map(k => [k, (fields as unknown as Record<string, unknown>)[k]])),
    } as ScenarioRowTemplate<ScenarioFieldBranch<{ states: FeedbackState[] }>>),
    onSync: (next) => {
      const current = initData(block.data);
      emit(versionStructure(block.data, {
        ...current, scenarioBranches: next,
      } as unknown as Record<string, unknown>));
    },
  });

  const remapModal = pendingLink ? (
    <ScenarioRemapModal
      versionCount={initData(block.data).versions?.length ?? 0}
      variantCount={((blocks ?? []).find(b => b.id === pendingLink)?.data as
        { versions?: unknown[] } | undefined)?.versions?.length ?? 0}
      leaderName={(blocks ?? []).find(b => b.id === pendingLink)?.name ?? ''}
      onCancel={() => setPendingLink(null)}
      onRemap={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'remap'); }}
      onLinkEmpty={() => { const id = pendingLink; setPendingLink(null); linkToLeader(id, 'empty'); }}
    />
  ) : null;

  const linkMenu = (
    <ScenarioLinkMenu
      block={block}
      blocks={blocks}
      value={storedForLink.followsBlockId}
      onChange={linkToLeader}
      emit={emit}
      applyVersion={applyTiltVersion}
    />
  );

  /** The row on screen — graded-only, like every answer key, read off the LEADER's pointer. */
  const activeScenario = gradingMode === 'graded' && storedForLink.scenarioBranches?.length
    ? (storedForLink.scenarioBranches.find(b => b.scenarioId === shownScenarioId)
      ?? storedForLink.scenarioBranches[0])
    : undefined;
  const activeScenarioVersion = activeScenario
    ? scenarioInForce(leaderVersions, activeScenario.scenarioId) as { name?: string } | null : null;
  const scenarioPager = activeScenario ? (
    <VersionSelector
      versions={(leaderVersions ?? []) as { id: string }[]}
      activeId={activeScenario.scenarioId}
      onSelect={setLeaderShown}
      noun='scenario'
    />
  ) : null;
  /**
   * The ghosted stem for the TEST surface, read off the shared pointer rather than the editor's.
   *
   * Deliberately NOT `activeScenarioVersion`, which is gated on graded mode because it names
   * the row whose ANSWER KEY is being authored. The prose is a different question: a
   * participation block still asks about the scenario, and a teacher testing one still has to
   * be able to read it.
   */
  /* Never in Preview — see `useScenarioFollower`'s own note on why this is not the call
     site's decision to make. */
  const ghostedScenario = isPreview ? null : (
    <ScenarioStimulus
      leader={(blocks ?? []).find(b => b.id === storedForLink.followsBlockId)}
      version={scenarioInForce(leaderVersions, shownScenarioId) as Record<string, unknown> | null}
      ghosted
    />
  );
  const dismissUnreviewed = () => {
    const current = initData(block.data);
    emit(versionStructure(block.data, {
      ...current,
      scenarioBranches: markScenarioReviewed(current.scenarioBranches ?? [], activeScenario!.scenarioId),
    } as unknown as Record<string, unknown>));
  };

  const emitFields = (next: Partial<TiltBlockEditorData>) => {
    const base = initData(block.data);
    /* A SCENARIO ROW takes the write before a version can, and the two cannot both apply
       (§2.2) — the same bargain `writeTiltVersion` makes, one selector over. See Numerical's
       own note for why the feedback goes into the row as well. */
    if (base.scenarioBranches?.length) {
      const merged = { ...base, ...next } as TiltBlockEditorData;
      const written = writeScenarioRow(
        base as unknown as Record<string, unknown>, shownScenarioId,
        merged as unknown as Record<string, unknown>, TILT_VERSION_KEYS,
      ) as Record<string, unknown>;
      if ('feedback' in next && shownScenarioId) {
        written.scenarioBranches = (written.scenarioBranches as ScenarioFieldBranch<{ states: FeedbackState[] }>[])
          .map(r => (r.scenarioId === shownScenarioId
            ? { ...r, feedback: next.feedback as { states: FeedbackState[] }, unreviewed: undefined } : r));
      }
      emit(written);
      return;
    }
    emit(writeTiltVersion(base, editorVersion, { ...base, ...next } as TiltBlockEditorData) as unknown as Record<string, unknown>);
  };

  const { settleLive, settleStored } = useIssueSettling(
    { answers: answerPositionsKey({ conditionType, answers, betweenAnswers, threshold }) },
    { answers: answerPositionsKey(initData(block.data)) },
  );

  const fields: TiltBlockEditorData = {
    question: questionHtml, visibilityMode, conditionType, answers, toleranceSymmetry, showTolerance, betweenAnswers, threshold,
    gradingMode, bypassEnabled,
    feedback: { states: [] }, // placeholder — real feedback pushed via currentDataRef below
  };
  const currentDataRef = useRef(fields);
  currentDataRef.current = fields;

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle = positiveSlotTitleFor(gradingMode, t);

  const {
    feedback, setFeedback, resyncFeedback, updateFeedbackState, deleteFeedbackState, getTitle,
  } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      // Sensor blocks say "measurement", not "question": a student who skips one of these hasn't
      // failed to answer, they've failed to TAKE A READING, and the feedback should name the thing
      // they actually have to do. Shared across GPS/Compass/Angle so the three can't drift apart.
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noMeasurementContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
      extra: {
        [NO_SENSOR_DATA_TRIGGER]: t('blocks.sensors.noSensorDataFeedback'),
      },
    },
    // Angle's first sensor state — see buildNormalizedTiltData, which seeds the same one for the
    // non-editing render paths. Both, or the panel and the test legend disagree about what exists.
    extraDefaults: TILT_SENSOR_DEFAULTS(t),
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      emitFields({ ...currentDataRef.current, feedback: nextFeedback });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;

  function push(overrides: Partial<TiltBlockEditorData> = {}) {
    emitFields({ ...currentDataRef.current, feedback, ...overrides });
  }

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleGradingMode(m: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, m, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(m);
    push({ gradingMode: m, feedback: { states: nextFeedbackStates } });
  }

  function handleBypassToggle() {
    const next = !bypassEnabled;
    setBypassEnabled(next);
    push({ bypassEnabled: next });
  }

  function setConditionTypeAndPush(next: TiltConditionType) {
    setConditionType(next);
    push({ conditionType: next });
  }
  /**
   * Flip ONE answer between a single tolerance and a pair, carrying its numbers across.
   *
   * Switching used to wipe the other representation — go asymmetric and the `±2` you typed was
   * gone, leaving two empty boxes that grade as an exact match. Numerical already copied the
   * value across; the angle blocks never did. Now that the toggle is per answer this is the one
   * place it happens, so all five get it.
   */
  function toggleAnswerSymmetry(id: string) {
    const next = answers.map(a => {
      if (a.id !== id) return a;
      return answerSymmetry(a, restored.data) === 'symmetric'
        ? { ...a, toleranceSymmetry: 'asymmetric' as const, toleranceBelow: a.toleranceValue, toleranceAbove: a.toleranceValue }
        : { ...a, toleranceSymmetry: 'symmetric' as const, toleranceValue: a.toleranceAbove || a.toleranceBelow };
    });
    setAnswers(next);
    push({ answers: next });
  }
  function updateAnswer(id: string, patch: Partial<ExactAnswer>) {
    const next = answers.map(a => a.id === id ? { ...a, ...patch } : a);
    setAnswers(next);
    push({ answers: next });
  }
  function addAnswer() {
    const next = [...answers, makeExactAnswer()];
    setAnswers(next);
    push({ answers: next });
  }
  function removeAnswer(id: string) {
    if (answers.length <= 1) return;
    const next = answers.filter(a => a.id !== id);
    setAnswers(next);
    push({ answers: next });
  }
  function updateBetweenAnswer(id: string, patch: Partial<BetweenAnswer>) {
    const next = betweenAnswers.map(a => a.id === id ? { ...a, ...patch } : a);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function addBetweenAnswer() {
    const next = [...betweenAnswers, makeBetweenAnswer()];
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function removeBetweenAnswer(id: string) {
    if (betweenAnswers.length <= 1) return;
    const next = betweenAnswers.filter(a => a.id !== id);
    setBetweenAnswers(next);
    push({ betweenAnswers: next });
  }
  function handleThresholdChange(v: string) {
    // NOT sanitised. This value comes from a FormulaField, so it may legitimately be an equation —
    // `{n}*30` — and `filterPositiveNumber` strips `{`, `}` and `*`, quietly reducing it to `30`.
    // The threshold IS the answer under lt/lte/gt/gte, and every other equation-valued answer in
    // the block accepts chips, so this one silently refusing them was a defect, not a policy.
    // Out-of-range is checked separately as an issue (`thresholdOutOfRange`).
    setThreshold(v);
    push({ threshold: v });
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = feedback.states.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /**
   * A new targeted state starts BLANK.
   *
   * It used to invent one: `lt:10`, then `lt:20`, then `lt:30`, counting up to dodge a collision
   * with a trigger already in the list. Every one of those is a real, gradeable condition the
   * teacher never authored — it fires on real student answers, shows up in Flow, and counts toward
   * coverage, all while reading as a suggestion. Numerical has seeded `exact:` (empty) all along;
   * this is the other two catching up.
   *
   * The dedupe loop went with it. It existed only to make the invented numbers distinct, and blank
   * triggers do not collide in any way that matters — states are keyed by `id`, and two unfinished
   * conditions are two unfinished conditions.
   */
  /** The card the Add button last produced, so it opens ready to type into — the owner's
   *  preference for this family, and the reason `startExpanded` exists. Keyed by mode, because
   *  the panel reorders its sections on a switch and the cards remount; a bare id would reopen
   *  the last one every time. State, not a ref: it is read during render. */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    // `=`, like every other block. It was `lt:` only because that was the type the invented
    // `lt:10` seed happened to use, which was never a decision about what a new condition means.
    const trigger = 'exact:';
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: tiltTargetedTitle(trigger, t),
      trigger, content: '',
      evaluation: currentDataRef.current.gradingMode === 'graded' ? 'incorrect' : 'neutral',
    };
    const next = { states: rebuildStates([...targeteds, newState]) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = feedback.states.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    const next = { states: rebuildStates(arrayMove(targeteds, from, to)) };
    setFeedback(next);
    emitFields({ ...currentDataRef.current, feedback: next });
  }

  function handleDone() {
    push();
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // Summarizes whichever condition is currently active into the same plain-text form used
  // for the collapsed answer bar and the locked Correct feedback card's chip. Unlike
  // slider-block.tsx's rawCorrectChip (which shows "value ± error"), an exact answer here
  // shows the *resolved* range (e.g. "9.8° to 10.2°") — clearer once the band can wrap
  // through the 0/360 seam ("358° to 2°" reads naturally; "0° ± 2°" would not).
  /**
   * The accepted answer, in words.
   *
   * `scenario` is the whole two-style rule in one parameter. A DISPLAY surface — the canvas card,
   * the editor at rest — passes null and sees the equation as unrolled chips, because the answer
   * genuinely is "{n}*30", not any one roll of it. The moment a teacher picks Minimum/Maximum/Random
   * the question changes to "what does it accept THIS time", and the honest answer is a band of
   * degrees. Both readings are true; which is showing is the scenario, exactly as numerical's bar
   * decides it.
   *
   * This took a bug report to find because the formula branch below returned early and never
   * consulted a scenario at all, so the bar sat on chips through every roll while the feedback
   * panel beside it resolved — one fact, two derivations, for the seventh time this week.
   */
  // A single representative angle to show in the preview/collapsed mockup's live-read
  // placeholder, standing in for what a student would actually see once answered correctly —
  // rather than a generic dash that tells a teacher nothing about what they just configured.
  // Only the first OR'd answer is shown (a live read is one number, not a list); 'between' has
  // no single authored value, so its range's midpoint is used; one-sided conditions have no
  // real "point" either, so the threshold itself stands in. Null when no real answer is
  // configured yet — the mockup falls back to the dash placeholder in that case, as before.
  function representativeCorrectValue(d: TiltBlockEditorData): number | null {
    if (isTiltSetupIncomplete(d)) return null;
    // `resolveAnswer`, not `num`: an equation answer would otherwise be shown as 0 degrees, which
    // is a number a teacher could reasonably believe. Null when it does not resolve — the mockup
    // already falls back to the dash placeholder.
    const only = (v: number) => (Number.isFinite(v) ? v : null);
    if (d.conditionType === 'exact') {
      const a = d.answers.find(a => a.value.trim() !== '');
      return a ? only(resolveAnswer(a.value)) : null;
    }
    if (d.conditionType === 'between') {
      const a = d.betweenAnswers.find(a => a.low.trim() !== '' && a.high.trim() !== '');
      if (!a) return null;
      const lo = resolveAnswer(a.low), hi = resolveAnswer(a.high);
      return Number.isFinite(lo) && Number.isFinite(hi) ? (lo + hi) / 2 : null;
    }
    return only(resolveAnswer(d.threshold));
  }

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // Genuinely interactive now — same widget and same evaluateTilt path the live test panel
    // uses, triggered by the page-level bottom Submit. Angle is scenario-simulated with no
    // sensor fallback at all, and unlike GPS/Compass that isn't a preview-only compromise: no
    // device-orientation capture exists in this block yet (the tilt-gesture capture is still a
    // design, not an implementation), so simulation is currently the only way to read it.
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const d = buildNormalizedTiltData(initData(block.data), t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const previewReading = testTaken ? (capturedDegrees ?? simTiltFor(d)) : null;
    const previewTest = computeTiltTestData(d, t, i18n.language, previewReading, hasSubmitted, bypassAnswer, resolveAnswer, rolledValues, tiltSensorDead, gradedOffAxis());
    /* See gps-block: the same derivation asked about the DRAFT, so Current says what would fire
       now rather than going blank until a reading is taken. */
    const draftTest = computeTiltTestData(d, t, i18n.language, previewReading, true, bypassAnswer, resolveAnswer, rolledValues, tiltSensorDead, gradedOffAxis());
    return (
      // No question render here — TiltAnswerDisplay owns it, so Preview cannot drift from the canvas
      // card or the phone Test tab the way it previously had.
      <div className='flex flex-col gap-3'>
        <TiltTestWidget
          scenario={ghostedScenario}
          d={d}
          // Preview reads the task's one device, so this block's own strip stays hidden. In the
          // editor there is no provider above us and this is false, which is what keeps per-block
          // testing exactly as it was.
          sharedDevice={!!shared}
          scenarioId={testScenario}
          mode={sourceMode}
          onModeChange={handleSourceModeChange}
          deviceStatusHint={deviceStatusHint}
          onScenarioChange={handleTestScenarioChange}
          hintReading={simTiltFor(d)}
          liveReading={liveTiltFor(d)}
          manual={simManualProps}
          sensorUi={sensorUiProps}
          reading={previewReading}
          onTakeReading={() => handleTakeReading(d)}
          onClearReading={handleClearReading}
          hasReading={testTaken}
          t={t}
          locale={i18n.language}
          bypass={bypassProps}
        >
          {/* See keyword-block: only the block knows which conditions are in force. */}
          <ReportTestChips chips={draftTest.testChips} states={draftTest.testResult} />
          {renderTiltFiredFeedback(previewTest.testResult)}
        </TiltTestWidget>
      </div>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = buildNormalizedTiltData(initData(block.data), t, shownVersionId);
    const previewValue = representativeCorrectValue(d);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    const collapsedIssues = settleStored(memoizedByKey(tiltIssuesCache, block.data, [t, variables, shownVersionId], () => tiltIssues(d, issueCtx)));
    const collapsedReading = testTaken ? (capturedDegrees ?? simTiltFor(d)) : null;
    const collapsedTest = computeTiltTestData(d, t, i18n.language, collapsedReading, hasSubmitted, bypassAnswer, resolveAnswer, rolledValues, tiltSensorDead, gradedOffAxis());
    const tiltAriaLabel = d.question
      ? t('blocks.tilt.editAriaBaseWithQuestion', { question: d.question.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) })
      : t('blocks.tilt.editAriaBase');

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<TiltIcon className='h-5 w-5 text-ws-primary dark:text-ws-text-link' />}
        titleLabel={t('blocks.tilt.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={tiltAriaLabel}
        answerBarSlot={() => tiltCollapsedBar(d, (
          // The collapsed card is a DISPLAY surface: roll `null`, so a variable answer shows
          // its equation as chips rather than one roll of it. Same derivation as the editor.
          <AnswerBarText
            chips={tiltChips(d, { trigger: 'correct' }, null, t, i18n.language,
              (d.feedback?.states ?? []).filter(x => x.type === 'targeted').map(x => x.trigger))}
            noValidAnswerLabel={answerExprTexts(d).length > 0 ? t('blocks.answers.noValidAnswer') : undefined}
          />
        ), t, collapsedIssues)}
        testEvaluation={collapsedTest.testResult ? (collapsedTest.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: collapsedTest.testChips,
          onReset: handleTestReset,
          // Reroll redraws the randomised value INSIDE the armed scenario — "another just-inside-
          // tolerance reading", not another scenario. It lives here rather than on the scenario control
          // because a <select> doesn't fire onChange when you re-pick what's already selected, and
          // because this is the same button that rerolls variables in every other block.
          onReroll: handleSimReroll,
          /* No branches here, so this row carries only the version pager and the way into
             editing it — the same component every banked block uses, which renders nothing at
             all for a block with no bank. */
          topNotices: (
            <VersionBranchNotice
              versions={testPager?.versions ?? shownVersions}
              activeVersionId={testPager ? testPager.activeId : shownVersionId}
              onSelectVersion={testPager?.onSelect ?? setTestVersion}
              noun={testPager?.noun ?? 'version'}
              editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={null}
              onEdit={() => {
                editShownVersion();
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={collapsedIssues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {/* DISPLAY STATE ONLY. It reserves the space the question will fill and reads as a normal
            authoring placeholder here, but a student must never see placeholder text — so it is gated on
            !isSelected, because a selected card IS the live test surface. The blank-question warning
            itself lives on the issue bar; this is just the reserved slot. */}
        {!isSelected && !d.question && (
          <EmptyQuestionNote />
        )}
        {isSelected ? (
          // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
          // task-level provider so every block on the page resolves against one shared roll.
          <BlockPreviewProvider value={rolledValues}>
          <TiltTestWidget
          scenario={ghostedScenario}
            d={d}
            scenarioId={testScenario}
            mode={sourceMode}
            onModeChange={handleSourceModeChange}
            deviceStatusHint={deviceStatusHint}
            onScenarioChange={handleTestScenarioChange}
            hintReading={simTiltFor(d)}
            liveReading={liveTiltFor(d)}
            manual={simManualProps}
            sensorUi={sensorUiProps}
            reading={collapsedReading}
            onTakeReading={() => handleTakeReading(d)}
            onClearReading={handleClearReading}
            hasReading={testTaken}
            t={t}
            locale={i18n.language}
            bypass={bypassProps}
          >
            {renderTiltFiredFeedback(collapsedTest.testResult)}
          </TiltTestWidget>
          </BlockPreviewProvider>
        ) : (
        /* The unselected card is the SAME answer display, rendered inactive — not a hand-built
           lookalike. The old copy had drifted into showing a Calibrate button that exists NOWHERE in
           the real widget (`calibrateButton` had exactly one use, right here), advertising a control a
           student is never offered; it was also the third item that made this row overflow a
           tablet-width card. A representative correct angle stands in for a reading. The actual
           correct-answer readout lives in the answer bar (tiltCollapsedBar), not here. */
        <TiltAnswerDisplay
          scenario={ghostedScenario}
          disabled
          d={d}
          reading={previewValue}
          hasReading={previewValue != null}
          t={t}
          locale={i18n.language}
        />
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Validation / derived state ──────────────────────────────────────────────

  // Live editor-state snapshot — what the teacher is currently typing, not the last-pushed
  // block.data, so the issue bar and the field highlights update on the same keystroke.
  const liveD: TiltBlockEditorData = {
    question: questionHtmlRef.current,
    visibilityMode, conditionType, answers, toleranceSymmetry, showTolerance, betweenAnswers, threshold, gradingMode, bypassEnabled, feedback,
  };

  // ── Variable testing ────────────────────────────────────────────────────────
  // Same Min/Max/Random tray numerical and slider carry, from the one shared component. Drives this
  // block's OWN roll (slider's model, not numerical's separate scenario) because the test surface
  // here already resolves through `rolledValues` — a second roll state would just be a second thing
  // to keep in sync.
  /** The live answer fields WITH their ids, so each resolves in its own angle mode. */
  const answerFieldRowsLive: { value: string; fieldId: string }[] =
    liveD.conditionType === 'exact'
      ? liveD.answers.map(a => ({ value: a.value, fieldId: `${a.id}.value` }))
      : liveD.conditionType === 'between'
        ? liveD.betweenAnswers.flatMap(a => [
          { value: a.low, fieldId: a.id }, { value: a.high, fieldId: a.id }])
        : [{ value: liveD.threshold, fieldId: 'threshold' }];
  const answerFieldsLive: string[] = answerFieldRowsLive.map(r => r.value);
  const hasChipFormulas = answerFieldsLive.some(hasVariableChip);
  const answerExtrema = hasChipFormulas ? computeFormulaExtrema({
      conditionType: liveD.conditionType, answers: liveD.answers, betweenAnswers: liveD.betweenAnswers,
      threshold: liveD.threshold, toleranceMode: 'absolute', toleranceSymmetry: liveD.toleranceSymmetry, angleMode: 'degrees',
  }, variables) : null;
  function applyVariableTestMode(mode: 'min' | 'max' | 'random') {
    // Drives BOTH: the scenario the feedback panel resolves against, and the block roll the test
    // surface grades against, so the two agree while a scenario is active.
    const next = mode === 'random' ? rollVariableValues(variables)
      : answerExtrema ? (mode === 'min' ? answerExtrema.loValues : answerExtrema.hiValues)
      : null;
    if (!next) return;
    setFeedbackTestValues(next);
    setRolledValues(next);
    setVariableTestMode(mode);
  }
  /** Resolves ONLY while a scenario is picked. With none, every formula stays NaN, which is what
   * makes the feedback panel fall through to showing the equation instead of a band. */
  const resolveFeedback = (v: string, fieldId: string): number =>
    feedbackTestValues ? evaluateFormulaRaw(v, feedbackTestValues, angleModeFor(liveD, fieldId)) : (hasVariableChip(v) ? NaN : num(v));

  /**
   * Where the numbers in the chips came from — `equation → value`, the shape numerical's scenario
   * bar uses and the branching blocks' test bars used before it.
   *
   * Shown only under a live scenario. At rest the equation is already on screen with its variables
   * unrolled, and repeating it here with nothing resolved would be the same line twice.
   */
  const scenarioCalculation: React.ReactNode = !feedbackTestValues ? null : (
    <div className='flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-xs text-ws-text'>
      <span className='flex flex-wrap items-center gap-x-1.5 gap-y-0.5'>
        {answerFieldsLive.filter(hasVariableChip).map((f, i) =>
          <span key={i}><FormulaWithValues formula={f} rolledValues={feedbackTestValues} /></span>)}
      </span>
      {(() => {
        const resolved = answerFieldRowsLive.filter(r => hasVariableChip(r.value))
          .map(r => evaluateFormulaRaw(r.value, feedbackTestValues, angleModeFor(liveD, r.fieldId)))
          .filter(Number.isFinite);
        return resolved.length === 0 ? null : (
          <span className='font-semibold tabular-nums'>
            {' → '}{resolved.map(n => `${fmtDegrees(n, i18n.language)}°`).join(', ')}
          </span>
        );
      })()}
    </div>
  );

  const variableTestController: React.ReactNode = !hasChipFormulas ? null : (
    <VariableTestController
      mode={variableTestMode}
      onSelect={applyVariableTestMode}
      onClear={() => { setVariableTestMode(null); setFeedbackTestValues(null); }}
      extremaAvailable={!!answerExtrema}
      calculation={scenarioCalculation}
    />
  );

  const editorIssues = settleLive(tiltIssues(liveD, issueCtx));

  // ── Phone "Test" tab ────────────────────────────────────────────────────────
  const phoneReading = isPhone && testTaken ? (capturedDegrees ?? simTiltFor(liveD)) : null;
  const phoneTest = isPhone ? computeTiltTestData(liveD, t, i18n.language, phoneReading, hasSubmitted, bypassAnswer, resolveAnswer, rolledValues, tiltSensorDead, gradedOffAxis()) : null;
  const phoneTestSlot = phoneTest ? (
    // Block-test roll only. Preview is deliberately NOT wrapped: it inherits the
    // task-level provider so every block on the page resolves against one shared roll.
    <BlockPreviewProvider value={rolledValues}>
    <TiltTestWidget
          scenario={ghostedScenario}
      d={liveD}
      scenarioId={testScenario}
      mode={sourceMode}
      onModeChange={handleSourceModeChange}
      deviceStatusHint={deviceStatusHint}
      onScenarioChange={handleTestScenarioChange}
      hintReading={simTiltFor(liveD)}
      liveReading={liveTiltFor(liveD)}
      manual={simManualProps}
      sensorUi={sensorUiProps}
      reading={phoneReading}
      onTakeReading={() => handleTakeReading(liveD)}
      onClearReading={handleClearReading}
      hasReading={testTaken}
      t={t}
      locale={i18n.language}
      bypass={bypassProps}
    >
      {renderTiltFiredFeedback(phoneTest.testResult)}
    </TiltTestWidget>
    </BlockPreviewProvider>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: handleSimReroll,
    /* The same row as the canvas card's. The EDITOR's pointer here: on a phone, Edit and Test
       are two tabs over ONE session. */
    topNotices: (
      <VersionBranchNotice
        versions={testPager?.versions ?? shownVersions}
        activeVersionId={testPager ? testPager.activeId : shownVersionId}
        onSelectVersion={testPager?.onSelect ?? selectVersion}
        noun={testPager?.noun ?? 'version'}
        editScrollsTo={shownScenarioId ? { fieldId: shownScenarioId, blockId: block.id } : undefined}
        stacked
        branch={null}
        onEdit={() => { editShownVersion(); setPhoneTestMode('edit'); }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  const acceptedRanges: TiltBoundedInterval[] = computeTiltAcceptedRanges(tiltAcceptedRangeConfig(liveD, resolveFeedback));
  /** With a formula answer and NO scenario picked, the feedback panel shows the equation — the
   * `lockedTriggerLabel` path, which `summarizeCorrect` now fills with the equation itself. A band
   * here would be a band around zero, and rerolling could never change it because nothing
   * downstream of the roll was being read. */
  const feedbackShowsEquation = tiltAnswerHasFormula(liveD) && feedbackTestValues === null;
  /** The equation as a FEEDBACK chip, not bare nodes. The collapsed card's bar and this panel want
   * different wrappers: the bar is plain text, a feedback trigger is always
   * [evaluation icon | divider | condition] — the shape every other block's chips use, and the one
   * slider's own `positiveChipNode` follows. Reusing the bar's nodes here rendered a naked formula
   * beside properly-chipped siblings. */


  // Angle's domain is the closed [0, 360] loop (see rotation.ts), so every gap already has two
  // real, finite endpoints — no more ±Infinity one-sided cases to special-case here.
  function formatTiltGap(gap: TiltInterval): string {
    return formatTiltRange(gap.lo, gap.hi, t, i18n.language);
  }

  const allTargetedTriggers = feedback.states.filter(s => s.type === 'targeted').map(s => s.trigger);
  const overlappingTriggerPairs = findTiltOverlappingFeedbackPairs(allTargetedTriggers);
  const overlappingTriggerSet = new Set(overlappingTriggerPairs.flat());

  // One deterministic assignment, the same one `getIssues` probes with, so the card and the bar
  // cannot reach different conclusions about the same condition.
  const cardProbeValues = probeRoll(variables);

  /**
   * The card's ERROR — a condition that is broken, not merely awkward.
   *
   * Same detector the issue bar counts, so the two cannot disagree. They were not connected at all
   * before: the bar named the fault for a block with several conditions in it and no card carried a
   * mark, leaving the teacher to open each one and compare.
   */
  function tiltFeedbackError(trigger: string, stateId: string): string | undefined {
    const fault = triggerFault(trigger, {
      resolve: (v: string) => evaluateFormulaRaw(v, cardProbeValues, angleModeFor(fields, `feedback.${stateId}.trigger`)),
      declared: new Set(variables.map(v => v.name)),
      probeable: new Set(Object.keys(cardProbeValues)),
      allowWrap: false,
    });
    return fault ? triggerFaultMessage(fault, t) : undefined;
  }

  /*
   * Both band verdicts need the band to be SPOKEN FOR. Graded always speaks for it — Correct is
   * checked first and is terminal — and outside graded only a Set Answer target claims it; with
   * no claimant there is nothing to overlap, because the catch-all is catching those readings
   * anyway. Compass and GPS have carried this gate for a while and Tilt never got it, so a
   * Participation block warned "Completely overlaps Correct" about a Correct it does not have.
   */
  const acceptedClaimed = gradingMode === 'graded'
    || feedback.states.some(x => x.type === 'targeted' && x.trigger === SET_ANSWER_TRIGGER);
  function tiltFeedbackWarning(trigger: string): string | undefined {
    /*
     * BOTH SIDES OF A COLLISION ARE LIT, which is what makes one findable. A value-vs-value pair
     * already did — `overlappingTriggerSet` is built from `.flat()` — and Set Answer was the
     * one-sided case: it states the band rather than overlapping it, which is true and is not the
     * point. A teacher looking at one lit card should not have to hunt for what it collides with.
     */
    if (trigger === SET_ANSWER_TRIGGER) {
      return acceptedClaimed && feedback.states.some(x => x.type === 'targeted' && x.trigger !== SET_ANSWER_TRIGGER
        && (isTiltFeedbackFullySubsumed(x.trigger, acceptedRanges) || doesTiltFeedbackOverlapAccepted(x.trigger, acceptedRanges)))
        ? t('blocks.tilt.feedbackOverlapWarning') : undefined;
    }
    if (!acceptedClaimed) return overlappingTriggerSet.has(trigger) ? t('blocks.tilt.feedbackOverlapWarning') : undefined;
    if (isTiltFeedbackFullySubsumed(trigger, acceptedRanges)) return t('blocks.tilt.feedbackSubsumedWarning');
    if (doesTiltFeedbackOverlapAccepted(trigger, acceptedRanges)) return t('blocks.tilt.feedbackOverlapsCorrectWarning');
    if (overlappingTriggerSet.has(trigger)) return t('blocks.tilt.feedbackOverlapWarning');
    return undefined;
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = feedback.states.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  /* The sensor states, in the order evaluateTilt gates them: nothing measured, then measured
     while the phone was held between its two axes. */
  const noSensorDataState = feedback.states.find(s => s.trigger === NO_SENSOR_DATA_TRIGGER);
  const noSensorDataCards = noSensorDataState ? [
    <SensorFeedbackCard key='no-data' state={noSensorDataState} getTitle={getTitle}
      label={t('blocks.sensors.noSensorDataFeedback')}
      onChange={patch => updateFeedbackState(noSensorDataState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];
  const poorAxisState = feedback.states.find(s => s.trigger === 'poor_axis');
  const poorSensorCards = poorAxisState ? [
    <SensorFeedbackCard key='off-axis' state={poorAxisState} getTitle={getTitle}
      label={t('blocks.tilt.feedbackTriggerPoorAxis', { cutoff: fmtLocale(TILT_OFF_AXIS_CUTOFF_DEG, i18n.language) })}
      onChange={patch => updateFeedbackState(poorAxisState.id, patch)}
      activeEditorRef={activeEditorRef} setActiveEditor={setActiveEditor} />,
  ] : [];
  // Mode-scoped: a NAMED trigger authored in one mode is hidden, not deleted, in the modes it
  // cannot fire in — see `targetedFiresIn`. Value triggers fire in every mode and all stay.
  const targetedStates = visibleTargeted(feedback.states, gradingMode);
  const canAddMore = targetedStates.length < 5;

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, targetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={targetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {targetedStates.map(s => {
            const ORDER = ['neutral', 'incorrect'] as const;
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                titleLocked
                /* Neutral and locked outside graded: the stored evaluation mirrors a verdict
                   the mode has not got, and there is no incorrect to cycle TO. */
                title={s.title} content={s.content}
                evaluation={displayEvaluation(gradingMode, s.evaluation)}
                evalLocked={gradingMode !== 'graded'}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                warning={tiltFeedbackWarning(s.trigger)}
                error={tiltFeedbackError(s.trigger, s.id)}
                triggerZone={<TiltTriggerEditor fieldId={`feedback.${s.id}.trigger`} answerTaken={targetedStates.some(x => x.id !== s.id && x.trigger === SET_ANSWER_TRIGGER)} onPlainFieldFocus={disconnectSharedToolbar} trigger={s.trigger} onChange={trigger => updateFeedbackState(s.id, { trigger, title: tiltTargetedTitle(trigger, t) })} t={t} variables={variables} rolledValues={rolledValues} gradingMode={gradingMode} answerConditionChips={<ConditionChipRow chips={neutralChips(tiltChips(fields, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, targetedStates.map(x => x.trigger)))} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />} />}
                /* The preset states the authored CONDITION, resolved against the roll, exactly
                   as graded's Correct card does — "-50° to -40°", not "< ?°". Set Answer holds no
                   value of its own (it IS the answer), so running it through the trigger chip
                   printed an operator with its operand missing. The expanded card already showed
                   the real chips through `answerConditionChips`, so collapsing the card changed
                   what it claimed. Same chips, same derivation, as Compass. */
                triggerZoneSummary={s.trigger === SET_ANSWER_TRIGGER ? (
                  <ConditionChipRow
                    chips={neutralChips(tiltChips(fields, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, targetedStates.map(x => x.trigger)))}
                    unreachableLabel={t('blocks.feedbackPanel.unreachable')}
                  />
                ) : <TiltTriggerChip angleMode={angleModeFor(fields, `feedback.${s.id}.trigger`)} trigger={s.trigger} evaluation={displayEvaluation(gradingMode, s.evaluation)} t={t} scenario={feedbackTestValues ?? {}} />}
                onTitleChange={() => {}}
                onContentChange={html => updateFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => updateFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
                onDelete={() => deleteFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // Correct genuinely cannot fire without a real configured answer (an unset exact/between/
  // threshold field defaults to 0 via num()) — shown with the same muted "Inactive" treatment
  // every other reachability state uses, rather than silently resolving to "0°", which would
  // misleadingly read as a real accepted angle. Same pattern as numerical-block.tsx's own
  // inactiveChip/hasConfiguredAnswer.
  const hasConfiguredAnswer = !isTiltSetupIncomplete(fields);
  const correctInactiveChip = (
    <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
  );
  const tiltPositiveInactive = gradingMode === 'graded' && !hasConfiguredAnswer;
  /** One chip per range, matching the legend and Compass's own correctChips/incorrectChips. The panel
   * used to pass a single `lockedTriggerLabel` holding every range joined into one string, so a
   * multi-answer block read as one long condition instead of the list of alternatives it is. */
  // "or" between the bands — a reading lands in exactly one of them, and the test bar names them
  // the same way (Compass's own panel chips have always been separated like this).
  const tiltFeedbackRangeChips = (ranges: TiltInterval[], evaluation: 'correct' | 'incorrect') =>
    ranges
      .filter(r => Number.isFinite(r.lo) && Number.isFinite(r.hi))
      .flatMap((r, i) => [
        ...(i > 0 ? [<span key={`${evaluation}-or-${i}`} className='self-center text-xs italic text-ws-muted'>{t('blocks.connectors.or')}</span>] : []),
        <span key={`${evaluation}-${i}`} className={`${TRIGGER_CHIP_CLASS} ${firedFeedbackEvalClass(evaluation)}`}>
          {chipEvalIcon(evaluation)}
          <ChipDivider />
          <span className={TRIGGER_CHIP_TEXT_CLASS}>
            {formatTiltRange(r.lo, r.hi, t, i18n.language)}
          </span>
        </span>,
      ]);

  // `lockedTriggerLabel` is typed `string` and cannot carry chips, so the graded case leaves it
  // undefined and puts its content through `triggerZone` instead — the same route slider's
  // `positiveChipNode` takes for exactly this reason.
  const tiltPositiveLabel =
    // Only the non-graded modes get a generic chip here; graded prints its actual answer, built
    // above. `undefined` is what lets that path stand.
    gradingMode !== 'graded' ? positiveSlotTitleFor(gradingMode, t) :
    undefined;

  // Hoisted: both default rows need the carve-outs, and this sat between them.
  const targetedTriggers = targetedStates.map(s => s.trigger);
  // `ConditionChipRow`, not `AnswerBarChips`: this is the feedback PANEL, where an unreachable
  // state is a condition being described and the Unreachable pill is the right reading. Only the
  // answer bar swaps it for bare text.
  /**
   * Outside graded the catch-all states its own name as a chip, through the shared derivation.
   *
   * It used to leave the row UNDEFINED and lean on `lockedTriggerLabel` to fill the gap, which
   * worked on the collapsed card and left the expanded one titled from storage — "Correct", on a
   * block that has no such thing. The label is the card's own title now, so the two agree.
   */
  /**
   * The participation catch-all ENUMERATES what it still catches, as every other value block's
   * does. It used to hand `catchAllChips` a single blanket chip reading "Any Tilt", which is the
   * one thing the catch-all is not allowed to say once something else is targeted: the standing
   * rule is that a teacher can read off exactly what the card covers and what it does not.
   *
   * `acceptedClaimed` is what decides whether the authored band comes out of the leftovers, and
   * it does once a Set Answer target owns it — the same call shape Compass and GPS use.
   */
  const tiltParticipationRow = gradingMode === 'graded' ? undefined : (
    <ConditionChipRow
      chips={catchAllChips(gradingMode, targetedStates.length > 0, t,
        () => tiltChips(liveD, { trigger: 'incorrect' }, feedbackTestValues, t, i18n.language,
          targetedTriggers, targetedStates.some(x => x.trigger === SET_ANSWER_TRIGGER)))}
      unreachableLabel={t('blocks.feedbackPanel.unreachable')}
    />
  );
  const correctPanelRow = tiltPositiveInactive ? correctInactiveChip
    : gradingMode !== 'graded' ? tiltParticipationRow
    : (
      <ConditionChipRow
        chips={tiltChips(liveD, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, targetedTriggers)}
        unreachableLabel={t('blocks.feedbackPanel.unreachable')}
      />
    );

  const correctSlot = feedback.states
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={tiltPositiveInactive}
        lockedTriggerLabel={tiltPositiveLabel}
        // ONE derivation, both modes. `feedbackTestValues` is already the ChipRoll — null at rest,
        // the scenario once picked — so the fork that stood here (a joined-string chip when
        // unrolled, per-range chips when rolled) is internal to `tiltChips` now.
        triggerZone={correctPanelRow}
        triggerZoneSummary={correctPanelRow}
        title={catchAllTitle(gradingMode, targetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /* The tick goes once a target exists, like every other block's catch-all. */
        evaluation={gradingMode !== 'graded' && targetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const defaultIncorrectUnreachable = isTiltDefaultIncorrectUnreachable(acceptedRanges, targetedTriggers);
  const defaultIncorrectGaps = computeTiltDefaultIncorrectGaps(acceptedRanges, targetedTriggers);
  const defaultIncorrectGapText = defaultIncorrectGaps.map(formatTiltGap).filter(Boolean).join(` ${t('blocks.connectors.or')} `);

  // With an unresolved equation there is no band to take the complement OF, so the gap maths runs
  // on whatever the formula parses to and reports a confident, wrong answer — here, that every
  // heading is covered, i.e. "Unreachable" for the state that actually catches everything. The
  // symbolic statement is the truthful one ("Anything but {n}*30"), and real bands take over the
  // moment a scenario is rolled. Same treatment as Compass, and as Numerical's own formula chip.
  // "The answer is an equation with no usable band right now" — which covers BOTH the state before
  // a scenario is rolled and a scenario that fails to resolve it. Keying only on "no scenario yet"
  // meant that rolling a formula the roll could not work out flipped the card straight back to the
  // false "Unreachable", which is the very thing being fixed.
  const answerBandUnusable = tiltAnswerHasFormula(liveD)
    && (acceptedRanges.length === 0 || acceptedRanges.some(r => !Number.isFinite(r.lo) || !Number.isFinite(r.hi)));
  const showSymbolicIncorrect = feedbackShowsEquation || answerBandUnusable;
  // ONE derivation, replacing the fork that stood here. See `tiltChips`.
  const incorrectFormulaChip: React.ReactNode = (
    <ConditionChipRow
      chips={tiltChips(liveD, { trigger: 'incorrect' }, feedbackTestValues, t, i18n.language, targetedTriggers)}
      unreachableLabel={t('blocks.feedbackPanel.unreachable')}
    />
  );
  const incorrectSlot = feedback.states
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={!showSymbolicIncorrect && defaultIncorrectUnreachable}
        lockedTriggerLabel={!showSymbolicIncorrect && !defaultIncorrectUnreachable ? defaultIncorrectGapText : undefined}
        triggerZone={showSymbolicIncorrect ? incorrectFormulaChip
          : defaultIncorrectUnreachable ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          : defaultIncorrectGaps.length > 0 ? tiltFeedbackRangeChips(defaultIncorrectGaps, 'incorrect') : undefined}
        triggerZoneSummary={showSymbolicIncorrect ? incorrectFormulaChip
          : defaultIncorrectUnreachable ? <UnreachableChip label={t('blocks.feedbackPanel.unreachable')} />
          : defaultIncorrectGaps.length > 0 ? tiltFeedbackRangeChips(defaultIncorrectGaps, 'incorrect') : undefined}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      icon={<TiltIcon className='h-6 w-6' />}
      titleLabel={t('blocks.tilt.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.tilt.closeEditor')}
      onClose={handleDone}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>

            {/* Two values, not GPS's three. Live Reading is absent because a clinometer is read with
                the phone pressed against something awkward, where hunting for Submit disturbs the
                pose. Capture on Submit hides the number, which is how a teacher asks for an angle set
                by eye. */}
            {/* The SHARED capture-mode vocabulary, not this block's own. Compass and GPS offer the
                same control with the same three words; Tilt simply omits Live Reading. */}
            <RadioGroup fieldId='visibilityMode'
              aria-label={t('blocks.captureMode.label')}
              value={visibilityMode}
              onChange={v => { const m = v as TiltVisibilityMode; setVisibilityMode(m); push({ visibilityMode: m }); }}
              options={[
                { value: 'capture', label: t('blocks.captureMode.capture') },
                { value: 'capture_on_submit', label: t('blocks.captureMode.onSubmit') },
              ]}
            />

            <CheckboxRow fieldId='bypassEnabled'
              checked={bypassEnabled}
              onToggle={handleBypassToggle}
              label={t('blocks.captureMode.allowBypass')}
            />
          </OptionsSection>

          <OptionsSection title={t('blocks.optionsSections.studentDisplay')}>

            {/* Disabled when the condition type is not `exact`: a between range or a threshold IS the
                answer, so showing it would disclose what was asked rather than how precisely to do it. */}
            <CheckboxRow fieldId='showTolerance'
              checked={showTolerance}
              onToggle={() => { const v = !showTolerance; setShowTolerance(v); push({ showTolerance: v }); }}
              label={t('blocks.tilt.showTolerance')}
              disabled={conditionType !== 'exact' || !settingAvailable('showTolerance', gradingMode)}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.tilt.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      // On the FRAME, not the feedback panel: the surface that most needs the numbers
      // interpreting is the one the answer bar is hidden behind.
      scenarioBarSlot={variableTestController}
      /**
       * The accepted answer, in the EDITOR — which this block did not have.
       *
       * Both blocks built an answer bar and gave it only to the collapsed canvas card, so the one
       * surface where a teacher is actually setting the answer was the one that never showed what
       * it had become. Numerical has had an editor bar all along; this is the same content through
       * the same shared bar, minus the card's mobile chip, and it sits under the scenario bar so a
       * rolled equation and the band it resolves to read as one thing.
       *
       * Issues are NOT repeated here: `warningBarSlot` renders them directly above, which is the
       * split the collapsed card cannot make and the editor can.
       */
      answerBarSlot={fields.gradingMode !== 'graded'
        ? collapsedModeBar(fields.gradingMode, t, 'tilt')
        : (() => {
            /*
             * `isTiltSetupIncomplete` is the WRONG test for this, and gating on it is what made an
             * incomplete range show no bar at all. It conflates two states the bar must separate:
             * "nothing authored yet" (no bar) and "authored, but nothing valid" ("No Valid Answer").
             * A `between` row with one bound filled is the second — the teacher typed something and
             * the evaluator accepts nothing — and it read as the first.
             *
             * `answerExprTexts` asks the honest question: is any answer field authored at all?
             */
            const chips = tiltChips(liveD, { trigger: 'correct' }, feedbackTestValues, t, i18n.language, targetedTriggers);
            const authored = answerExprTexts(liveD).length > 0;
            if (!answerBarHasContent(chips, authored)) return null;
            return (
              <CollapsedBlockBar tone='success' align='start' scrollX>
                <AnswerBarText chips={chips}
                  noValidAnswerLabel={authored ? t('blocks.answers.noValidAnswer') : undefined} />
              </CollapsedBlockBar>
            );
          })()}
      feedbackPanelProps={{
        // Angle's first sensor card. Nothing here before, because nothing could fire.
        noSensorDataSlot: <>{noSensorDataCards}</>,
        poorSensorSection: poorSensorCards.length > 0
          ? {
            // Named only when there is more than one, so the heading says what they share rather
            // than repeating a lone card's own label back at it.
            heading: poorSensorCards.length > 1 ? t('blocks.sensors.poorSectionHeading') : undefined,
            content: <>{poorSensorCards}</>,
          }
          : undefined,
        noAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          isEmpty: targetedStates.length === 0,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
            {/* The three-way offer, when linking meets an existing bank — §7.1. */}
            {remapModal}
            {/* The link control and what it points at, for BOTH kinds of link — see
                `VariesWithSection`, which decides on its own whether a DRIVEN block
                needs it. This was five lines of identical JSX in twelve blocks. */}
            <VariesWithSection
              following={!!activeScenario}
              linkMenu={linkMenu}
              stimulus={<ScenarioStimulus leader={(blocks ?? []).find(b => b.id === storedForLink.followsBlockId)}
                  version={activeScenarioVersion as Record<string, unknown> | null} />}
            />
            {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
                the answer setup, because a driven axis varies the question too. Renders its
                children bare when this block is not driven. See `DrivenRowFrame`. */}
            <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
            {/* A bank is a niche feature: in the heading it is available without being advertised. */}
            <EditorSectionHeading
              label={t('blocks.areas.question')}
              divider={!!activeScenario}
              action={(
                <div className='flex items-center gap-2'>
                {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2). */}
                {!storedForLink.followsBlockId && (
                <VersionSelector
                  versions={(initData(block.data).versions ?? []) as { id: string }[]}
                  activeId={editorVersionId}
                  onSelect={selectVersion}
                  onAdd={() => {
                    const next = addTiltVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                    emit(versionStructure(block.data, next));
                    const added = next.versions?.[next.versions.length - 1];
                    if (added) selectVersion(added.id, next);
                  }}
                  onRemove={(id) => {
                    const next = removeTiltVersion(initData(block.data), id);
                    emit(versionStructure(block.data, next));
                    const landing = next.versions?.[0];
                    selectVersion(landing?.id, next);
                    setTestVersion(landing?.id);
                  }}
                />
                )}
                {/* Silent once there is a Varies-With section, which is where the
                    control moves to — see `QuestionHeadingLink`. Written as
                    `!activeScenario` it knew about scenarios only, so a DRIVEN block
                    showed the link twice and the heading overflowed sideways. */}
                <QuestionHeadingLink
                  following={!!activeScenario}
                  linkMenu={linkMenu}
                />
                </div>
              )}
            />
            <BlockQuestionEditor
              value={questionHtml}
              onChange={html => {
                // The render-time SNAPSHOT has to move with the ref, not just the ref.
                // `currentDataRef.current` is rebuilt during render, and this handler runs before any
                // re-render, so every push path would otherwise spread the PREVIOUS question and leave
                // the persisted value one keystroke behind what is on screen. MCQ/Dropdown/Keyword avoid
                // this by reading questionHtmlRef live at push time; these blocks bake the question into
                // `fields` instead, so the two have to be kept in lockstep here.
                questionHtmlRef.current = html;
                setQuestionHtml(html);
                currentDataRef.current = { ...currentDataRef.current, question: html };
                push();
              }}
              toolbar={sharedToolbar}
              editorRef={questionEditorRef}
              shouldFocusRef={shouldFocusEditor}
              embedded={embedded}
            />

            {/*
              * ANSWER SETUP, hidden in OPTIONAL only.
              *
              * Optional is the one mode with no use for an answer key. Survey keeps it: Set Answer
             * targets the authored band, so the band is live there even though nothing is graded. So the band and its tolerance have nothing to
              * act on. Hidden rather than greyed, because the whole SECTION's job is gone — the same
              * call the feedback panel already makes for its correctness cards in optional. A single
              * control that merely has no effect is disabled in place instead.
              *
              * Nothing is deleted: switch back to Has Correct and the answer is as it was.
              */}
            {gradingMode !== 'optional' && (<>
              {/* Armed-delete announcement — matches slider-block.tsx's exact pattern. */}
              <div aria-live='assertive' aria-atomic='true' className='sr-only'>
                {(armedAnswerId || armedBetweenId) ? t('blocks.actions.deleteArmed') : ''}
              </div>

              <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

              {/* The scenario's own box, around the ANSWER and nothing else — the sensor's
                  settings describe the instrument rather than the answer, so they stay outside
                  it exactly as they stay outside a version. */}
              <ScenarioSetupFrame
                scenario={activeScenario}
                name={activeScenarioVersion?.name}
                pager={scenarioPager}
                unreviewedCount={unreviewedCount(storedForLink.scenarioBranches)}
                onDismissUnreviewed={dismissUnreviewed}
              >
              <AnswerSetupFields
                variables={variables}
                rolledValues={rolledValues}
                onPlainFieldFocus={disconnectSharedToolbar}
                t={t}
                conditionType={conditionType}
                onConditionTypeChange={setConditionTypeAndPush}
                answers={answers}
                onUpdateAnswer={updateAnswer}
                onAddAnswer={addAnswer}
                onRemoveAnswer={removeAnswer}
                onToggleAnswerSymmetry={toggleAnswerSymmetry}
                betweenAnswers={betweenAnswers}
                onUpdateBetweenAnswer={updateBetweenAnswer}
                onAddBetweenAnswer={addBetweenAnswer}
                onRemoveBetweenAnswer={removeBetweenAnswer}
                threshold={threshold}
                onThresholdChange={handleThresholdChange}
                armedAnswerId={armedAnswerId}
                setArmedAnswerId={setArmedAnswerId}
                armedBetweenId={armedBetweenId}
                setArmedBetweenId={setArmedBetweenId}
              />
              </ScenarioSetupFrame>
            </>)}
            </DrivenRowFrame>
          </div>
      }
    />
  );
}
